<template>
  <!-- 登录弹窗 -->
  <LoginModal
    :visible="showLogin"
    :title="loginTitle"
    :message="loginMessage"
    @close="showLogin = false"
    @logged-in="onLoggedIn"
  />

  <!-- 禅模式确认弹窗 -->
  <div class="zen-enter-overlay" :class="{ visible: showZenConfirm }" @click.self="cancelZenEnter">
    <div class="zen-enter-modal">
      <div class="zen-enter-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <circle cx="12" cy="12" r="10"/>
          <circle cx="12" cy="12" r="3"/>
          <path d="M12 2a10 10 0 0 1 0 20" stroke-width="1" opacity="0.3"/>
          <path d="M12 2a10 10 0 0 0 0 20" stroke-width="1" opacity="0.15"/>
        </svg>
      </div>
      <div class="zen-enter-title">进入禅模式</div>
      <div class="zen-enter-subtitle">
        关闭多角色联动，专注于一对一的深度对话。<br>
        慢慢聊，不着急。
      </div>
      <div class="zen-enter-tips">
        <div><svg class="zen-tip-icon-svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg> 自动折叠侧边栏，沉浸式体验</div>
        <div><svg class="zen-tip-icon-svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg> 单角色深度对话，无调度干扰</div>
        <div><svg class="zen-tip-icon-svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg> 暖色调界面，降低视觉压力</div>
        <div><svg class="zen-tip-icon-svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><polyline points="9 10 12 13 15 10"/></svg> 更长上下文记忆，不丢细节</div>
      </div>
      <div class="zen-enter-actions">
        <button class="zen-btn-cancel" @click="cancelZenEnter">取消</button>
        <button class="zen-btn-enter" @click="confirmEnterZen">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right:4px;vertical-align:middle"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3"/></svg>
          进入禅模式
        </button>
      </div>
    </div>
  </div>

  <!-- 禅模式过渡动画 -->
  <div class="zen-transition-overlay" ref="zenTransitionRef">
    <div class="zen-ripple-circle"></div>
  </div>

  <!-- 主界面 -->
  <div id="app-layout" class="app-layout" :class="{ ready, 'zen-active': isZenActive }">

    <!-- 侧边栏 -->
    <Sidebar ref="sidebarRef" @open-settings="showSettings = true" @open-profile="showProfile = true" @open-content-page="openContentPage" @open-creative-detail="onOpenCreativeDetail" @open-shares="onOpenShares" @open-gallery="onOpenGallery" />

    <!-- 移动端遮罩 -->
    <div
      class="sidebar-overlay"
      :class="{ visible: mobileSidebarOpen }"
      @click="toggleMobileSidebar"
    ></div>

    <!-- 右侧主区域 -->
    <div class="main-content">
      <!-- 禅模式退出按钮 -->
      <button v-if="isZenActive" class="zen-exit-btn" @click="exitZenMode" title="退出禅模式">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        退出禅模式
      </button>

      <!-- 移动端顶部工具栏 -->
      <div class="top-toolbar" id="top-toolbar">
        <div class="toolbar-left">
          <button class="btn-sidebar-toggle" id="btn-sidebar-toggle" title="菜单" @click="toggleMobileSidebar">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 6h18M3 12h18M3 18h18"/></svg>
          </button>
        </div>
        <span class="toolbar-logo" id="toolbar-logo" v-html="nameBarText"></span>
        <div class="toolbar-right">
          <button class="btn-toolbar-share" id="btn-toolbar-share" title="分享对话" @click="toggleShareSelect" v-show="!shareSelectMode">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
          </button>
          <button class="btn-toolbar-new-chat" id="btn-toolbar-new-chat" title="新建对话" @click="newChat">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          </button>
        </div>
      </div>

      <!-- 助手名称状态栏 -->
      <div class="assistant-name-bar" id="assistant-name-bar" v-html="nameBarText"></div>

      <!-- AI 风险提示 -->
      <div class="disclaimer-banner" id="disclaimer-banner" v-if="!disclaimerClosed">
        <span class="disclaimer-icon">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
        </span>
        <span id="disclaimer-text">{{ disclaimerText }}</span>
        <button id="disclaimer-close" title="关闭" @click="disclaimerClosed = true">×</button>
      </div>

      <!-- 进度横幅 -->
      <div class="progress-overlay hidden" id="progress-overlay">
        <div class="progress-card" id="progress-card" v-html="progressHTML"></div>
      </div>

      <!-- 右侧节点导航 -->
      <div class="chat-nav-dots" id="chat-nav-dots" ref="navDotsRef"></div>

      <!-- 对话区域 -->
      <div class="chat-area" id="chat-area" :class="{ 'input-centered': inputCentered }">
        <div class="chat-messages" id="chat-messages" ref="chatMessagesRef">

          <!-- 创作成果详情 -->
          <div class="creative-detail-view" v-if="rightPanel === 'detail'">
            <button class="creative-detail-back" @click="restoreChatSnapshot()" title="返回">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
              返回对话
            </button>
            <div class="creative-detail-meta">
              <span class="creative-detail-badge">{{ roleTypeLabel(creativeDetail.role_key) }}</span>
              <span class="creative-detail-date">{{ creativeDetail.created_at }}</span>
            </div>
            <div class="creative-detail-prompt" v-if="creativeDetail.prompt">
              <div class="creative-detail-prompt-label">创作提示词</div>
              <div class="creative-detail-prompt-text">{{ creativeDetail.prompt }}</div>
            </div>
            <div class="creative-detail-media" :class="{ 'is-video': creativeDetail.is_video }">
              <template v-if="creativeDetail.is_video">
                <video :src="creativeDetail.url" controls class="creative-detail-video">
                  您的浏览器不支持视频播放
                </video>
              </template>
              <template v-else>
                <img :src="creativeDetail.url" :alt="creativeDetail.prompt" class="creative-detail-img" @click="openCreativeLightbox">
              </template>
            </div>
            <div class="creative-detail-actions">
              <button class="btn-creative-download" @click="downloadCreativeMedia">下载原文件</button>
              <button class="btn-creative-share" @click="shareCreativeMedia">分享</button>
            </div>
          </div>

          <!-- 我的分享面板（右侧主内容区） -->
          <div class="right-panel" v-else-if="rightPanel === 'shares'">
            <div class="right-panel-header">
              <button class="right-panel-back" @click="restoreChatSnapshot()" title="返回对话">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
                返回对话
              </button>
              <h2 class="right-panel-title">我的分享</h2>
            </div>
            <div class="right-panel-body">
              <div class="share-list-loading" v-if="shareListLoading">加载中…</div>
              <div class="share-list-empty" v-else-if="myShares.length === 0">
                <p>暂无分享记录</p>
                <p class="share-list-hint">在对话页面点击分享按钮创建</p>
              </div>
              <div class="share-list" v-else>
                <div class="share-list-item" v-for="s in myShares" :key="s.share_id">
                  <div class="share-list-item-info">
                    <div class="share-list-item-title" :title="s.title">{{ s.title }}</div>
                    <div class="share-list-item-meta">
                      <span class="share-list-item-date">{{ s.created_at?.slice(0, 10) }}</span>
                      <span class="share-list-item-id">ID: {{ s.share_id }}</span>
                    </div>
                  </div>
                  <div class="share-list-item-actions">
                    <button class="share-list-btn" @click="handleCopyShareUrl(s.url)" title="复制链接">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                      复制链接
                    </button>
                    <button class="share-list-btn share-list-btn-open" @click="handleOpenShareUrl(s.url)" title="在新标签页打开">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                      打开
                    </button>
                    <button class="share-list-btn share-list-btn-delete" @click="handleDeleteShare(s.share_id)" title="删除">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                      删除
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 创作成果画廊面板（右侧主内容区） -->
          <div class="right-panel" v-else-if="rightPanel === 'gallery'">
            <div class="right-panel-header">
              <button class="right-panel-back" @click="restoreChatSnapshot()" title="返回对话">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
                返回对话
              </button>
              <h2 class="right-panel-title">创作成果</h2>
            </div>
            <div class="right-panel-body">
              <div class="gallery-grid-main" v-if="!galleryLoading && galleryItems.length > 0">
                <div class="gallery-card" v-for="(item, i) in galleryItems" :key="i" @click="openGalleryDetail(item)">
                  <img v-if="!item.is_video" :src="item.url" :alt="item.prompt || 'AI创作'" loading="lazy" @error="(e) => (e.target as HTMLImageElement).style.display='none'">
                  <div class="gallery-card-video" v-else>
                    <img :src="item.url + '?x-oss-process=video/snapshot,t_0,f_jpg'" :alt="item.prompt || 'AI视频'" loading="lazy" @error="(e) => (e.target as HTMLImageElement).style.display='none'">
                    <span class="gallery-card-play"><svg width="20" height="20" viewBox="0 0 24 24" fill="#fff" stroke="none"><polygon points="5 3 19 12 5 21 5 3"/></svg></span>
                  </div>
                  <div class="gallery-card-prompt" v-if="item.prompt">{{ item.prompt?.substring(0, 30) }}{{ item.prompt?.length > 30 ? '…' : '' }}</div>
                </div>
              </div>
              <div class="gallery-empty-main" v-else-if="!galleryLoading">
                <p>暂无创作成果</p>
              </div>
              <div class="gallery-loading-main" v-else>加载中…</div>
            </div>
          </div>

          <!-- 正常对话内容 -->
          <template v-else>
          <div class="empty-state" v-if="inputCentered && !portal.state.isStreaming">
            <template v-if="isZenActive">
              <div class="empty-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--zen-accent, #c4956a)" stroke-width="1.5" opacity="0.5">
                  <circle cx="12" cy="12" r="10"/>
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M12 2a10 10 0 0 1 0 20" stroke-width="1" opacity="0.3"/>
                  <path d="M12 2a10 10 0 0 0 0 20" stroke-width="1" opacity="0.15"/>
                </svg>
              </div>
              <h3>有什么想聊的？</h3>
              <p class="empty-desc-pc">我在这里，慢慢说，不着急</p>
            </template>
            <template v-else>
              <div class="empty-icon">
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" stroke-width="1.5" opacity="0.6">
                  <path d="M12 2a7 7 0 0 0-7 7c0 2.4 1.2 4.6 3 5.9V17l3-2 3 2v-2.1c1.8-1.3 3-3.5 3-5.9a7 7 0 0 0-7-7z"/>
                  <circle cx="9" cy="9" r="1" fill="currentColor" stroke="none"/>
                  <circle cx="15" cy="9" r="1" fill="currentColor" stroke="none"/>
                  <path d="M9 12h6" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none"/>
                </svg>
              </div>
              <h3>智能对话，即刻开始</h3>
              <p class="empty-desc-pc">AI 自动调度多智能体，为您提供一站式服务</p>
            </template>
            <div class="empty-desc-mobile">
              <div class="empty-site-title">{{ siteName }}</div>
              <div class="empty-site-subtitle" v-if="siteSubtitle" :style="{ display: siteSubtitle ? '' : 'none' }">{{ siteSubtitle }}</div>
            </div>
          </div>
          </template>
        </div>
        <!-- 右侧对话导航定位点 -->
        <div class="chat-nav-dots" id="chat-nav-dots" ref="navDotsRef"></div>
      </div>

      <!-- AI创作结果区 -->
      <div class="create-panel hidden" id="create-panel">
        <div class="create-panel-inner" id="create-panel-inner">
          <div id="create-chat-history" class="create-chat-history"></div>
          <div class="create-result" id="create-result"></div>
        </div>
      </div>

      <!-- 输入区域 -->
      <div class="input-area" id="input-area" :class="{ 'input-centered': inputCentered }">
        <div class="input-container">
          <!-- 输入区上方：标题 + 副标题 + 快捷问题 -->
          <div class="input-header" v-show="inputCentered">
            <div class="input-chat-title">{{ chatHeaderTitle || 'Hi, 我是小鹿' }}</div>
            <div class="input-chat-subtitle" v-if="chatHeaderSubtitle">{{ chatHeaderSubtitle }}</div>
            <div class="input-chat-subtitle" v-else>今天从哪里开始？</div>
            <div class="chat-suggestions" v-if="chatSuggestions.length > 0">
              <button
                v-for="(q, i) in chatSuggestions"
                :key="i"
                class="chat-suggestion-btn"
                @click="sendSuggestion(q)"
              >{{ q }}</button>
            </div>
          </div>
          <!-- 文件预览条 -->
          <div class="file-preview-bar hidden" id="file-preview-bar"></div>
          <!-- @提及标签条 -->
          <div class="mention-chips hidden" id="mention-chips"></div>
          <div class="input-box">
            <textarea
              id="chat-input"
              ref="chatInputRef"
              v-model="inputText"
              :placeholder="isZenActive ? '想说点什么… 慢慢来，不着急' : '输入消息… (@唤角色, Enter 发送, Shift+Enter 换行)'"
              rows="1"
              :disabled="inputDisabled"
              @keydown="onInputKeydown"
              @input="onInputInput"
              @compositionstart="isComposing = true"
              @compositionend="onCompositionEnd"
              @paste="onPaste"
              @dragover.prevent
              @drop="onDrop"
            ></textarea>
            <div class="input-actions">
              <div class="input-tools">
                <button class="input-tool-btn" id="btn-upload-toggle" title="上传文件" @click="toggleUploadMenu">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
                </button>
                <div class="upload-menu hidden" id="upload-menu">
                  <button class="upload-menu-item" @click.stop="openFilePicker('image')">图片</button>
                  <button class="upload-menu-item" @click.stop="openFilePicker('file')">文件</button>
                </div>
                <button class="input-tool-btn" id="btn-mention" title="@提及角色" @click="insertAtSymbol">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="4"/><path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-3.92 7.94"/></svg>
                </button>
              </div>
              <div class="input-mode" id="mode-selector-area">
                <div class="mode-switch-bar">
                  <div class="mode-switch-group">
                    <button
                      class="mode-btn mode-fast"
                      :class="{ active: portal.state.gatewayMode === 'fast' }"
                      @click="switchGatewayMode('fast')"
                      title="快速响应，使用轻量模型"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg> 快速
                    </button>
                    <button
                      class="mode-btn mode-expert"
                      :class="{ active: portal.state.gatewayMode === 'expert' }"
                      @click="switchGatewayMode('expert')"
                      title="深度分析，使用高级模型"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="8" r="7"/><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"/></svg> 专家
                    </button>
                  </div>
                </div>
              </div>
              <button class="btn-send" id="btn-send" title="发送" @click="sendMessage" :style="{ display: isStreaming ? 'none' : 'flex' }">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 2 11 13"/><path d="M22 2 15 22l-4-9-9-4 20-4z"/></svg>
              </button>
              <button class="btn-stop" id="btn-stop" title="停止生成" @click="stopGeneration" :style="{ display: isStreaming ? 'flex' : 'none' }">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="1"/></svg>
              </button>
            </div>
          </div>
          <div class="input-status" id="input-status"></div>
        </div>
      </div>

      <!-- AI创作输入区 -->
      <div class="create-input-area hidden" id="create-input-area">
        <div class="create-input-container">
          <!-- Tab 切换 -->
          <div class="create-tabs" id="create-tabs">
            <button :class="['create-tab', { active: createTab === 'text2img' }]" @click="switchCreateTab('text2img')">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg> 文生图
            </button>
            <button :class="['create-tab', { active: createTab === 'img2img' }]" @click="switchCreateTab('img2img')">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg> 图生图
            </button>
            <button :class="['create-tab', { active: createTab === 'video' }]" @click="switchCreateTab('video')">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="5 3 19 12 5 21 5 3"/></svg> 文生视频
            </button>
            <button :class="['create-tab', { active: createTab === 'video_i2v' }]" @click="switchCreateTab('video_i2v')">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="7" width="20" height="13" rx="2" ry="2"/><polyline points="17 2 12 7 7 2"/></svg> 图生视频
            </button>
          </div>
          <!-- 图生图上传预览区 -->
          <div class="create-upload-area hidden" id="upload-area" @click="triggerImageUpload">
            <input type="file" accept="image/*" id="i2i-file-input" ref="i2iFileInput" style="display:none" @change="previewImage">
            <div class="create-upload-placeholder" id="i2i-placeholder">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>
              <span>点击上传图片</span>
            </div>
            <img class="create-preview hidden" id="i2i-preview" alt="预览" ref="i2iPreview">
          </div>
          <!-- 提示语 -->
          <div class="create-hint" id="create-hint">{{ createHint }}</div>
          <!-- 输入框 + 工具栏 -->
          <div class="create-input-box">
            <textarea id="create-prompt" ref="createPromptRef" v-model="createPrompt" :placeholder="createPlaceholder" rows="3"></textarea>
            <div class="create-input-actions">
              <div class="create-tool-row" id="size-row">
                <select class="create-select" v-model="createSize">
                  <option value="1024x1024">1:1 方形</option>
                  <option value="1024x768">4:3 横版</option>
                  <option value="768x1024">3:4 竖版</option>
                  <option value="1280x720">16:9 宽屏</option>
                </select>
              </div>
              <div class="create-tool-row hidden" id="video-params-row">
                <select class="create-select" v-if="createTab === 'video'" v-model="videoModel">
                  <option value="wan2.7-t2v">Wan2.7-T2V</option>
                  <option value="wan2.7-t2v-2026-04-25">Wan2.7-T2V(新)</option>
                  <option value="happyhorse-1.0-t2v">HappyHorse-T2V</option>
                </select>
                <select class="create-select" v-if="createTab === 'video_i2v'" v-model="videoI2vModel">
                  <option value="wan2.7-i2v">Wan2.7-I2V</option>
                  <option value="wan2.7-i2v-2026-04-25">Wan2.7-I2V(新)</option>
                  <option value="happyhorse-1.0-i2v">HappyHorse-I2V</option>
                </select>
                <select class="create-select" v-model="videoResolution">
                  <option value="720P">720P</option>
                  <option value="1080P">1080P</option>
                </select>
                <select class="create-select" v-model="videoRatio">
                  <option value="16:9">16:9 横版</option>
                  <option value="9:16">9:16 竖版</option>
                  <option value="1:1">1:1 方形</option>
                  <option value="4:3">4:3</option>
                  <option value="3:4">3:4</option>
                </select>
                <select class="create-select" v-model="videoDuration">
                  <option value="3">3秒</option>
                  <option value="5">5秒</option>
                  <option value="8">8秒</option>
                  <option value="10">10秒</option>
                  <option value="15">15秒</option>
                </select>
              </div>
              <button class="btn-create" id="btn-create" @click="doCreate" :disabled="creating" v-html="createBtnText">
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- AI生成内容提示 -->
      <div class="ai-disclaimer" id="ai-disclaimer">内容由AI生成，请仔细甄别</div>
    </div>
  </div>

  <!-- 设置面板 -->
  <SettingsPanel :visible="showSettings" @close="showSettings = false" @openContentPage="openContentPage" />

  <!-- 个人中心面板 -->
  <ProfilePanel :visible="showProfile" @close="showProfile = false" />

  <!-- 内容页面（用户协议/隐私政策/帮助与反馈） -->
  <ContentPage :visible="showContentPage" :type="contentPageType" @close="showContentPage = false" />

  <!-- 图片灯箱 -->
  <ImageLightbox :visible="showLightbox" :image-url="lightboxUrl" @close="showLightbox = false" />

  <!-- Toast 容器 -->
  <ToastContainer />

  <!-- 分享选择栏（底部悬浮） -->
  <div class="share-select-bar" :class="{ active: shareSelectMode }">
    <span class="share-bar-count">已选 {{ shareSelectedIndices.size }} 条</span>
    <div class="share-bar-actions">
      <button class="share-bar-btn share-bar-cancel" @click="cancelShareSelect">取消</button>
      <button class="share-bar-btn share-bar-confirm" :disabled="shareSelectedIndices.size === 0 || shareCreating" @click="createShare">
        {{ shareCreating ? '创建中…' : '生成分享链接' }}
      </button>
    </div>
  </div>

  <!-- 分享弹窗 -->
  <div class="share-modal-overlay" v-if="shareModalOpen" @click.self="shareModalOpen = false">
    <div class="share-modal">
      <h3>分享链接已生成</h3>
      <div class="share-modal-link">
        <input type="text" :value="shareLink" readonly ref="shareLinkInput" @click="$event.target.select()">
        <button @click="copyShareLink">复制</button>
      </div>
      <div class="share-modal-actions">
        <button class="btn-secondary" @click="cancelShareSelect">完成</button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, nextTick, watch, onUnmounted, provide } from 'vue'
import { usePortal } from '@/stores/portal'
import { useToast, setGlobalToast, globalToast } from '@/composables/useToast'
import Sidebar from '@/components/Sidebar.vue'
import LoginModal from '@/components/LoginModal.vue'
import SettingsPanel from '@/components/SettingsPanel.vue'
import ProfilePanel from '@/components/ProfilePanel.vue'
import ContentPage from '@/components/ContentPage.vue'
import ImageLightbox from '@/components/ImageLightbox.vue'
import ToastContainer from '@/components/ToastContainer.vue'
import { PORTAL_CONFIG } from '@/config'

const portal = usePortal()
const { toast } = useToast()
setGlobalToast((msg, type, duration) => toast(msg, type as any, duration))

// 中文角色名 -> roleKey 映射，用于 summary 引用链接跳转
const roleNameKeyMap = new Map<string, string>()

// ============ Declare globals ============
declare const marked: any
declare const katex: any

const CFG = PORTAL_CONFIG

// 响应式助手名称（解决 CFG 是普通对象 computed 不追踪变化的问题）
const assistantName = ref(CFG.ASSISTANT_NAME || CFG.SITE_NAME || '')

// ============ Refs ============
const sidebarRef = ref<any>(null)
const chatMessagesRef = ref<HTMLElement>()
const chatInputRef = ref<HTMLTextAreaElement>()
const zenTransitionRef = ref<HTMLElement>()
const navDotsRef = ref<HTMLElement>()
const shareLinkInput = ref<HTMLInputElement>()
const createPromptRef = ref<HTMLTextAreaElement>()
const i2iFileInput = ref<HTMLInputElement>()
const i2iPreview = ref<HTMLImageElement>()

// ============ State ============
const ready = ref(false)
const showLogin = ref(false)
const loginTitle = ref('欢迎回来')
const loginMessage = ref('请登录以继续')
const showSettings = ref(false)
const showProfile = ref(false)
const showContentPage = ref(false)
const contentPageType = ref<'terms' | 'privacy' | 'help'>('help')
// Zen mode
const showZenConfirm = ref(false)
const isZenActive = ref(false)
const zenEntering = ref(false)
const showLightbox = ref(false)
const lightboxUrl = ref('')
const mobileSidebarOpen = ref(false)
const disclaimerClosed = ref(false)
const disclaimerText = ref(CFG.DISCLAIMER || 'AI生成内容仅供参考')
const siteName = ref(CFG.SITE_NAME || '')
const siteSubtitle = ref(CFG.SITE_SUBTITLE || '')
const chatHeaderTitle = ref('')
const chatHeaderSubtitle = ref('')
const chatSuggestions = ref<string[]>([])
const inputText = ref('')
const inputCentered = ref(true)
const inputDisabled = ref(false)
const progressHTML = ref('')
let inputEverSent = false
let isComposing = false // 中文输入法组合输入中，跳过 autoResize 避免光标错位

// Chat state
const messages = reactive<any[]>([])
let typewriterAborted = false
let tabHidden = false
let userScrolledUp = false
let currentConfirmRequestId: string | null = null
let pendingConfirmMessage: string = ''  // 保存触发 awaiting_confirm 时的用户消息，避免 DOM 查询取到旧消息
let pendingConfirmFiles: any[] = []     // 保存触发 awaiting_confirm 时的文件列表
let pendingMultimodalAnswer: string = '' // 保存多模态分析结果文本，供专家分析时作为上下文
const feedbackCache: Record<string, number> = {}

// File state
const pendingFiles = reactive<any[]>([])
let mentionedRoles: any[] = []

// Mention state
let showMentionDropdown = false
let mentionSearchTimer: any = null
let mentionSearchResults: any[] = []

// Video polling
const videoPollTimers: Record<string, any> = {}

// Create state
const createTab = ref('text2img')
const createHint = ref('输入画面描述，AI 为您生成图片')
const createPlaceholder = ref('例如：一只橘猫趴在窗台上，午后阳光透过窗帘洒下，温暖写实风格...')
const createBtnText = ref('<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg> 生成图片')
const createPrompt = ref('')
const createSize = ref('1024x1024')
const creating = ref(false)
const i2iFile = ref<File | null>(null)
let videoTaskId: string | null = null
let videoPollTimer: any = null
let currentGroupId: string | null = null

// Video params
const videoModel = ref('wan2.7-t2v')
const videoI2vModel = ref('wan2.7-i2v')
const videoResolution = ref('720P')
const videoRatio = ref('16:9')
const videoDuration = ref('5')

// ---- 分享功能 ----
const shareSelectMode = ref(false)         // 是否在选择模式
const shareSelectedIndices = ref<Set<number>>(new Set())  // 选中的消息索引
let shareAllWrappers: HTMLElement[] = []   // 当前所有消息 wrapper
const shareModalOpen = ref(false)          // 分享弹窗
const shareLink = ref('')                  // 生成的分享链接
const shareCreating = ref(false)           // 是否正在创建

// ---- 创作成果详情 ----
const creativeDetail = ref<any>(null)     // 当前查看的创作成果详情

// ---- 右侧面板 ----
type RightPanel = 'chat' | 'shares' | 'gallery' | 'detail'
const rightPanel = ref<RightPanel>('chat')

// 切换面板前保存对话状态快照，返回时恢复
interface ChatSnapshot { messages: any[]; inputCentered: boolean; inputEverSent: boolean }
const chatSnapshot: ChatSnapshot = { messages: [], inputCentered: true, inputEverSent: false }
let hasSnapshot = false

// 我的分享列表
const myShares = ref<any[]>([])
const shareListLoading = ref(false)

// 创作成果画廊
const galleryItems = ref<any[]>([])
const galleryLoading = ref(false)

// ============ Computed ============
const isStreaming = computed(() => portal.state.isStreaming)
const nameBarText = computed(() => {
  const name = assistantName.value || CFG.SITE_NAME || ''
  const prefix = name ? name + ' · ' : ''
  const mode = portal.state.globalMode
  if (mode === 'zen') {
    return prefix + '<svg class="mode-label-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3"/></svg> 禅'
  }
  if (mode === 'debate') {
    return prefix + '深度思辨'
  }
  if (mode === 'create') {
    const tabName = document.querySelector('.create-tab.active')?.textContent?.trim() || 'AI创作'
    return prefix + tabName
  }
  const gm = portal.state.gatewayMode || 'fast'
  const modeLabel = gm === 'expert'
    ? '<svg class="mode-label-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="8" r="7"/><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"/></svg> 专家'
    : '<svg class="mode-label-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg> 快速'
  return prefix + modeLabel
})

// ============ Methods ============

// ---- Auth ----
function showLoginModalFn(message?: string, title?: string) {
  loginMessage.value = message || '请登录以继续'
  loginTitle.value = title || '欢迎回来'
  showLogin.value = true
}

// 提供给子组件（Sidebar 等）调用登录弹窗
provide('showLogin', showLoginModalFn)

async function onLoggedIn() {
  await portal.loadSiteConfig()
  applySiteConfigToUI()
  sidebarRef.value?.loadHistory()
  sidebarRef.value?.loadRoles()
  sidebarRef.value?.loadUserProfile()
  updateLoginUI()
  chatInputRef.value?.focus()
}

function updateLoginUI() {
  const adminBtn = document.getElementById('btn-admin')
  if (adminBtn) adminBtn.style.display = portal.isAdmin.value ? '' : 'none'
}

// ---- Theme ----
function initTheme() {
  const saved = localStorage.getItem('portal_theme') || 'light'
  portal.setTheme(saved as 'light' | 'dark')
}

// ---- Site Config ----
function applySiteConfigToUI() {
  const cfg = portal.state.siteConfig || {} as any
  const C = CFG
  const name = cfg.site_name || C.SITE_NAME || ''
  const desc = cfg.site_description || C.SITE_DESCRIPTION || ''
  const kw = cfg.site_keywords || C.SITE_KEYWORDS || ''
  const st = cfg.site_subtitle || C.SITE_SUBTITLE || ''
  const disc = cfg.disclaimer_text || C.DISCLAIMER || 'AI生成内容仅供参考，不可作为法律证据或专业意见。请谨慎甄别信息。'
  const asst = cfg.assistant_name || C.ASSISTANT_NAME || name

  C.ASSISTANT_NAME = asst
  assistantName.value = asst  // 同步更新响应式 ref，确保 computed 能追踪变化
  C.SITE_SUBTITLE = st

  // 同步 URL 配置（之前遗漏了，导致用户协议/隐私政策等链接始终为 '#'）
  C.HELP_URL = cfg.help_url || C.HELP_URL
  C.DOWNLOAD_URL = cfg.download_url || C.DOWNLOAD_URL
  C.TERMS_URL = cfg.terms_url || C.TERMS_URL
  C.PRIVACY_URL = cfg.privacy_url || C.PRIVACY_URL

  siteName.value = name
  siteSubtitle.value = st
  disclaimerText.value = disc
  chatHeaderTitle.value = cfg.chat_header_title || ''
  chatHeaderSubtitle.value = cfg.chat_header_subtitle || ''

  // 快捷问题
  let suggestions: string[] = []
  if (cfg.chat_suggestions) {
    try {
      const parsed = typeof cfg.chat_suggestions === 'string'
        ? JSON.parse(cfg.chat_suggestions)
        : cfg.chat_suggestions
      if (Array.isArray(parsed)) suggestions = parsed
    } catch (_) {}
  }
  chatSuggestions.value = suggestions
  document.title = name + (desc ? ' - ' + desc : ' - 智能对话')

  // Update meta
  let metaDesc = document.querySelector('meta[name="description"]')
  if (!metaDesc) {
    metaDesc = document.createElement('meta')
    metaDesc.setAttribute('name', 'description')
    document.head.appendChild(metaDesc)
  }
  metaDesc.setAttribute('content', desc || name)

  if (kw) {
    let metaKw = document.querySelector('meta[name="keywords"]')
    if (!metaKw) {
      metaKw = document.createElement('meta')
      metaKw.setAttribute('name', 'keywords')
      document.head.appendChild(metaKw)
    }
    metaKw.setAttribute('content', kw)
  }

  // Logo
  document.querySelectorAll('.sidebar-logo').forEach(el => {
    const logo = cfg.site_logo || C.SITE_LOGO || ''
    if (logo) {
      el.innerHTML = `<img src="${escHtml(logo)}" alt="${escHtml(name)}" style="height:80px;width:240px;object-fit:contain">`
    } else if (name) {
      el.innerHTML = `<span>${escHtml(name)}</span>`
    }
  })
}

// ---- Sidebar ----
function toggleMobileSidebar() {
  mobileSidebarOpen.value = !mobileSidebarOpen.value
  sidebarRef.value?.toggleMobile()
}

function newChat() {
  inputText.value = ''
  messages.length = 0
  portal.state.currentRequestId = null
  currentConfirmRequestId = null
  pendingConfirmMessage = ''
  hideNavDots()
  pendingConfirmFiles = []
  pendingMultimodalAnswer = ''
  creativeDetail.value = null
  rightPanel.value = 'chat'
  if (shareSelectMode.value) cancelShareSelect()
  portal.newConversationId()
  sidebarRef.value?.clearCurrentConv()
  // 清空 DOM 中的旧气泡
  const container = chatMessagesRef.value
  if (container) container.querySelectorAll('.message-wrapper,.phase-separator').forEach((el: Element) => el.remove())
  sidebarRef.value?.loadHistory()
  centerInputArea()
  chatInputRef.value?.focus()
}

// ---- Chat ----
function centerInputArea() {
  inputCentered.value = true
  inputEverSent = false
  const chatArea = document.getElementById('chat-area')
  if (chatArea) chatArea.classList.add('input-centered')
}

function uncenterInputArea() {
  if (inputEverSent) return
  inputEverSent = true
  inputCentered.value = false
  const chatArea = document.getElementById('chat-area')
  if (chatArea) chatArea.classList.remove('input-centered')
}

function autoResize() {
  const el = chatInputRef.value
  if (!el) return
  const pos = el.selectionStart
  el.style.height = 'auto'
  // Math.ceil 避免小数截断导致高度不够，光标偏到下一行
  const target = Math.ceil(Math.min(el.scrollHeight, 200))
  el.style.height = target + 'px'
  // 高度变化后恢复光标位置
  try { el.setSelectionRange(pos, pos) } catch (_) {}
}

function onInputKeydown(e: KeyboardEvent) {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    sendMessage()
  }
  if (e.ctrlKey && e.key === 'Enter') {
    e.preventDefault()
    sendMessage()
  }
  if (e.key === 'Escape' && portal.state.isStreaming) {
    e.preventDefault()
    stopGeneration()
  }
}

function onInputInput(e: Event) {
  // 中文输入法组合输入过程中跳过 autoResize，避免高度变化导致光标错位
  if (!isComposing) autoResize()
  handleMentionInput(e)
}

function onCompositionEnd(e: CompositionEvent) {
  isComposing = false
  // 组合输入结束后再调整高度
  autoResize()
  handleMentionInput(e as any)
}

function switchGatewayMode(mode: 'fast' | 'expert') {
  portal.state.gatewayMode = mode
}

/** 点击快捷问题按钮 */
function sendSuggestion(question: string) {
  if (!question || portal.state.isStreaming) return
  inputText.value = question
  nextTick(() => sendMessage())
}

async function sendMessage() {
  if (portal.state.isStreaming) return
  if (portal.isDemo.value) {
    globalToast('演示环境为只读模式，仅可浏览历史内容', 'info')
    return
  }
  if (!portal.isLoggedIn.value) {
    showLoginModalFn('请登录后发送消息', '请登录')
    return
  }

  typewriterAborted = false
  tabHidden = false
  userScrolledUp = false
  currentConfirmRequestId = null
  pendingConfirmMessage = ''
  pendingConfirmFiles = []
  pendingMultimodalAnswer = ''
  // 重置角色计数，防止上一轮多角色的残留值导致单角色出现空总结占位
  ;(window as any).__totalDispatchedRoles = 0
  // 清空角色名映射
  roleNameKeyMap.clear()

  const message = inputText.value.trim()
  if (!message && pendingFiles.length === 0) return

  inputText.value = ''
  inputDisabled.value = true
  autoResize()

  // Add user bubble
  appendMessage('user', message || '(图片/文件)', 'user')
  setTimeout(updateNavDots, 100)

  // Add file prev
  const currentFiles = [...pendingFiles]
  if (currentFiles.length > 0) {
    const wrapper = chatMessagesRef.value?.lastElementChild as HTMLElement
    if (wrapper) addFilePreviewToWrapper(wrapper, currentFiles)
    pendingFiles.length = 0
    renderFilePreviewsDOM()
  }

  // 保存当前消息和文件，供后续 awaiting_confirm → confirmMultimodal 使用
  pendingConfirmMessage = message
  pendingConfirmFiles = currentFiles

  uncenterInputArea()

  // Build body
  const isDebateMode = portal.state.globalMode === 'debate'
  const isZenMode = isZenActive.value
  const body: any = {
    message: message,
    conversation_id: portal.getOrCreateConversationId(),
    mode: isDebateMode ? 'debate' : (isZenMode ? 'zen' : 'dispatcher'),
    gateway_mode: isDebateMode ? 'expert' : (portal.state.gatewayMode || 'fast'),
    history: collectHistory(),
  }
  if (currentFiles.length > 0) {
    body.files = currentFiles.map(f => ({
      url: f.url, type: f.type, name: f.name,
      size: f.size, extracted_text: f.extracted_text || null,
    }))
  }
  if (mentionedRoles.length > 0) {
    body.mentioned_roles = mentionedRoles.map(r => r.role_key)
    mentionedRoles = []
    updateMentionChipsDOM()
  }

  showProgress(isDebateMode ? 'expert' : portal.state.gatewayMode)
  const requestRoles = new Map<string, any>()

  // 深度思辨模式 — 独立的 SSE 回调
  if (isDebateMode) {
    const totalSteps = 5
    let currentStep = 0
    let phaseTimer: ReturnType<typeof setInterval> | null = null
    let phaseStartTime = 0

    // 每个阶段的子步骤滚动提示
    const phaseHints: Record<string, string[]> = {
      decompose: ['识别问题类型…', '拆解核心维度…', '构造子问题列表…', '标记需确认项…'],
      proposal:  ['分析子问题…', '检索相关知识…', '设计方案选项…', '权衡利弊…'],
      critique:  ['检查逻辑一致性…', '扫描遗漏因素…', '评估执行成本…', '验证可行性…'],
      revision:  ['吸收批判意见…', '修正方案缺陷…', '补充遗漏细节…', '优化最终方案…'],
      verdict:   ['回溯检查…', '裁定核心分歧…', '生成累计共识…', '整理行动建议…'],
    }
    // 每个阶段的预计耗时（秒），用于倒计时
    const phaseDurations: Record<string, number> = {
      decompose: 30, proposal: 45, critique: 45, revision: 60, verdict: 120,
    }

    // SVG 图标集（与项目风格一致：stroke-based, 24×24）
    const svgIcons: Record<string, string> = {
      judge: '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="3" x2="12" y2="8"/><circle cx="12" cy="13" r="4"/><line x1="8" y1="21" x2="16" y2="21"/></svg>',
      builder: '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="12 2 22 8.5 22 15.5 12 22 2 15.5 2 8.5 12 2"/><line x1="12" y1="22" x2="12" y2="14"/><polyline points="22 8.5 12 14 2 8.5"/></svg>',
      reviewer: '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>',
    }

    // 阶段信息映射
    const phaseInfo: Record<string, { label: string, icon: string, cssClass: string }> = {
      decompose: { label: '裁判官拆解', icon: svgIcons.judge, cssClass: 'debate-calibrator' },
      proposal:  { label: '建构者方案', icon: svgIcons.builder, cssClass: 'debate-left' },
      critique:  { label: '审视者批判', icon: svgIcons.reviewer, cssClass: 'debate-right' },
      revision:  { label: '建构者修订', icon: svgIcons.builder, cssClass: 'debate-left' },
      verdict:   { label: '裁判官审定', icon: svgIcons.judge, cssClass: 'debate-calibrator' },
    }

    function startPhaseTimer(phase: string) {
      stopPhaseTimer()
      phaseStartTime = Date.now()
      const duration = phaseDurations[phase] || 60
      const card = document.getElementById('progress-card')
      // 获取或创建计时器元素
      let timerEl = card?.querySelector('.debate-timer') as HTMLElement | null
      if (!timerEl && card) {
        timerEl = document.createElement('span')
        timerEl.className = 'debate-timer'
        card.appendChild(timerEl)
      }
      if (!timerEl) return
      // 更新计时器，用已过时间/预估比例
      const updateTimer = () => {
        const elapsed = Math.floor((Date.now() - phaseStartTime) / 1000)
        if (elapsed < duration) {
          const pct = Math.min(Math.floor((elapsed / duration) * 100), 90)
          timerEl!.innerHTML = sIcons.clock+' 约'+pct+'%'
        } else {
          timerEl!.innerHTML = sIcons.clock+' 处理中…'
        }
      }
      updateTimer()
      phaseTimer = setInterval(updateTimer, 3000)
    }

    function stopPhaseTimer() {
      if (phaseTimer) { clearInterval(phaseTimer); phaseTimer = null }
      const timerEl = document.querySelector('#progress-card .debate-timer') as HTMLElement | null
      if (timerEl) timerEl.innerHTML = sIcons.check
    }

    function renderProgressDots(step: number, hint: string, phase?: string) {
      let dots = '<div class="debate-progress">'
      for (let i = 1; i <= totalSteps; i++) {
        if (i < step) dots += '<span class="prog-dot done"><svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="4"><polyline points="20 6 9 17 4 12"/></svg></span>'
        else if (i === step) dots += '<span class="prog-dot active"></span>'
        else dots += '<span class="prog-dot"></span>'
      }
      dots += `<span class="debate-hint">${escHtml(hint)}</span>`
      // 如果提供了phase，附加子步骤滚动提示
      if (phase && phaseHints[phase]) {
        dots += `<span class="debate-sub-hint" id="debate-sub-hint">↻ ${escHtml(phaseHints[phase][0])}</span>`
      }
      dots += '</div>'
      return dots
    }

    // 滚动切换子提示
    function cycleSubHint(phase: string) {
      const hints = phaseHints[phase]
      if (!hints || hints.length < 2) return
      let idx = 0
      const el = document.getElementById('debate-sub-hint')
      const interval = setInterval(() => {
        idx = (idx + 1) % hints.length
        if (el) {
          el.style.opacity = '0'
          setTimeout(() => {
            if (el) { el.textContent = '↻ ' + hints[idx]; el.style.opacity = '0.7' }
          }, 300)
        }
      }, 3000)
      return interval
    }

    let subHintInterval: ReturnType<typeof setInterval> | null = null

    portal.createSSEConnection(body, {
      onStart: (event) => updateProgress(event),
      onMessage: (event) => updateProgress(event),
      onPhase: (event) => {
        currentStep = event.step || 1
        const phase = event.phase || ''
        // 确保进度条可见
        const overlay = document.getElementById('progress-overlay')
        if (overlay) overlay.classList.remove('hidden')
        progressHTML.value = renderProgressDots(currentStep, event.hint || event.label || '', phase)
        // 启动阶段计时器 + 子步骤滚动
        startPhaseTimer(phase)
        if (subHintInterval) { clearInterval(subHintInterval); subHintInterval = null }
        subHintInterval = cycleSubHint(phase)
      },
      onPhaseDone: (event) => {
        // 停止计时器，打勾
        stopPhaseTimer()
        if (subHintInterval) { clearInterval(subHintInterval); subHintInterval = null }
        // 强制完成当前打字，避免与新气泡重叠
        typewriterAborted = true
        setTimeout(() => { typewriterAborted = false }, 60)

        const phase = event.phase || ''
        const info = phaseInfo[phase] || { label: phase, icon: '', cssClass: '' }

        // 阶段分隔线
        addDebatePhaseSeparator(info.label, info.cssClass, info.icon)

        if (phase === 'decompose') {
          const intro = event.intro || ''
          const subProblems = event.sub_problems || []
          let content = intro ? `<p>${escHtml(intro)}</p>` : ''
          if (subProblems.length > 0) {
            content += '<ol class="debate-sub-list">'
            subProblems.forEach((sp: any) => {
              const confirmTag = sp.needs_confirm ? ' <span class="debate-confirm-tag">需确认</span>' : ''
              content += `<li>${escHtml(sp.question)}${confirmTag}</li>`
            })
            content += '</ol>'
          }
          const bubbleEl = appendMessage('ai', content, '', false, `ai debate-html ${info.cssClass}`) as HTMLElement
          if (bubbleEl) scrollToBottom()
        } else {
          const content = event.content || ''
          if (content) {
            const bubbleEl = appendMessage('ai', '', '', false, `ai markdown ${info.cssClass}`) as HTMLElement
            if (bubbleEl) {
              typewrite(bubbleEl, content, () => { scrollToBottom(); setTimeout(updateNavDots, 200) })
            }
          }
        }
        // 非 typewrite 路径也刷新导航
        if (phase === 'decompose') setTimeout(updateNavDots, 150)
      },
      onDone: (event) => {
        hideProgress()
        if (event.status === 'debate_complete') {
          const convId = portal.state.currentConversationId
          if (convId) {
            fetch('/api/dashboard.php?action=generate_context', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + portal.getToken() },
              body: JSON.stringify({ group_id: convId }),
            }).then(r => r.json()).catch(() => {})
          }
          portal.state.currentRequestId = event.request_id || null
          resetInput()
          sidebarRef.value?.loadHistory()
        }
      },
      onError: (msg) => {
        appendMessage('ai', '<span class="debate-error-icon">!</span> ' + escHtml(msg), '系统提示', false, 'ai')
        resetInput()
      },
      onStop: () => {
        appendMessage('ai', '已停止生成', '系统提示', false, 'ai')
        resetInput()
      },
      onTokenWarning: (msg) => globalToast(msg, 'error'),
    })
    return
  }

  // 调度官/普通模式 — 原有回调
  portal.createSSEConnection(body, {
    onStart: (event) => updateProgress(event),
    onMessage: (event) => updateProgress(event),
    onDispatcherResult: (event) => {
      const roles = event.parsed_roles || []
      const count = roles.length
      const brief = event.brief || ''
      // 记录总角色数，用于后续进度计算
      ;(window as any).__totalDispatchedRoles = count
      progressHTML.value = `<span class="pulse-dot"></span>调度官分析完成 · 正在调度 ${count} 位专家回答...` +
        (brief ? ` · ${escHtml(brief)}` : '') +
        `<br><small>${roles.map((r: any) => '· ' + (r.role_name || r.role)).join(' ')}</small>`
    },
    onRoleDone: (event) => {
      const roleKey = event.role_key || event.role || 'unknown'
      const response = event.response || event.role_response || event.content || ''
      const annotatedContent = event.annotated_content || response
      const isSummary = event.is_summary == 1
      const roleName = event.role_name || roleKey
      requestRoles.set(roleKey, { response, annotatedContent, isSummary, roleName })
      roleNameKeyMap.set(roleName, roleKey)
      if (!isSummary) {
        // 仅当调度官分配了多个角色时才需要联动总结
        const totalRoles = (window as any).__totalDispatchedRoles || 0
        // 兜底：onDispatcherResult 还没触发时，用 requestRoles + 1 估算
        const estimatedTotal = totalRoles > 0 ? totalRoles : requestRoles.size + 1
        const needSummary = estimatedTotal > 1
        if (needSummary && requestRoles.size === 1) {
          addPhaseSeparator('联动总结')
          const summaryPlaceholder = appendMessage('ai',
            '<div class="summary-placeholder">'+sIcons.spin+' 正在收集专家意见...<span class="summary-progress">1/'+estimatedTotal+'</span></div>',
            '联动总结', false, 'summary markdown') as HTMLElement
          if (summaryPlaceholder) {
            summaryPlaceholder.setAttribute('data-summary-placeholder', '1')
          }
        }
        if (needSummary) updateSummaryPlaceholder(requestRoles.size)

        // 创建角色气泡（默认折叠，打字在折叠框内进行）
        const bubbleEl = appendMessage('ai', '', roleName, false, 'ai markdown') as HTMLElement
        if (bubbleEl) {
          // 先添加操作按钮（折叠按钮），再折叠
          addBubbleActions(bubbleEl, roleKey)
          // 立即折叠（打字在折叠框内进行）
          collapseBubble(bubbleEl)
          typewrite(bubbleEl, response, () => {
            // 用带编号的内容渲染锚点ID（在折叠框内保留完整HTML）
            renderAnnotatedBubble(bubbleEl, annotatedContent, roleKey)
            // 更新折叠按钮状态为"展开"
            updateCollapseBtn(bubbleEl, true)
          })
        }

        // 更新进度：角色回答完成 X/N
        const completedCount = requestRoles.size
        const totalForProgress = (window as any).__totalDispatchedRoles > 0
          ? (window as any).__totalDispatchedRoles
          : Math.max(completedCount, 3) // 兜底：避免 total=0 导致显示 "X/..."
        if (totalForProgress > 0) {
          progressHTML.value = `<span class="pulse-dot"></span>专家回答中... ${completedCount}/${totalForProgress} 完成`
        } else {
          progressHTML.value = `<span class="pulse-dot"></span>专家回答中... ${completedCount} 完成`
        }
      }
    },
    onDone: (event) => {
      if (event.status === 'awaiting_confirm') {
        hideProgress()
        currentConfirmRequestId = event.request_id || null
        showConfirmButtons(event)
        resetInput()
        sidebarRef.value?.loadHistory()
        return
      }
      if (event.status === 'no_multimodal') {
        currentConfirmRequestId = null
        pendingConfirmMessage = ''
        pendingConfirmFiles = []
        pendingMultimodalAnswer = ''
        appendMessage('ai', event.message || event.combined || '无权限', '系统提示', false, 'ai')
        resetInput()
        return
      }
      if (event.status === 'user_rejected') {
        currentConfirmRequestId = null
        pendingConfirmMessage = ''
        pendingConfirmFiles = []
        pendingMultimodalAnswer = ''
        resetInput()
        sidebarRef.value?.loadHistory()
        return
      }
      if (event.dispatcher_mode && event.status === 'ask' && event.combined) {
        appendMessage('ai', event.combined, CFG.ASSISTANT_NAME, false, 'ai markdown')
        portal.state.currentRequestId = event.request_id || null
        resetInput()
        sidebarRef.value?.loadHistory()
        return
      }
      if (event.dispatcher_mode && event.status === 'self_reply' && event.combined) {
        appendMessage('ai', event.combined, CFG.ASSISTANT_NAME, false, 'ai markdown')
        portal.state.currentRequestId = event.request_id || null
        resetInput()
        sidebarRef.value?.loadHistory()
        return
      }

      // Summary（仅调度官分配了多个角色时）
      const summaryText = event.summary || ''
      const totalRolesForSummary = (window as any).__totalDispatchedRoles > 0
        ? (window as any).__totalDispatchedRoles
        : Math.max(requestRoles.size, 2)
      if (summaryText && totalRolesForSummary > 1) {
        // 更新占位气泡为"正在进行总结，请稍后..."
        updateSummaryPlaceholderToGenerating()
        progressHTML.value = '<span class="pulse-dot"></span>正在进行联动总结，请稍后...'
        // 找到占位气泡，用总结内容替换
        const summaryBubble = findSummaryPlaceholderBubble()
        if (summaryBubble) {
          summaryBubble.setAttribute('data-summary-placeholder', '')
          typewrite(summaryBubble, summaryText, () => {
            // 渲染 summary 中的角色引用链接
            renderSummaryRefLinks(summaryBubble)
            addBubbleActions(summaryBubble, 'summary')
            // 总结完成，更新状态
            progressHTML.value = '<span style="color:var(--success)">'+sIcons.check+' 联动总结已完成，角色回答已折叠，可展开查看</span>'
            setTimeout(hideProgress, 3000)
          })
        } else {
          addPhaseSeparator('联动总结')
          const bubbleEl = appendMessage('ai', '', '联动总结', false, 'summary markdown') as HTMLElement
          if (bubbleEl) {
            typewrite(bubbleEl, summaryText, () => {
              renderSummaryRefLinks(bubbleEl)
              addBubbleActions(bubbleEl, 'summary')
              progressHTML.value = '<span style="color:var(--success)">'+sIcons.check+' 联动总结已完成，角色回答已折叠，可展开查看</span>'
              setTimeout(hideProgress, 3000)
            })
          }
        }
      } else {
        // 无总结内容，直接隐藏
        hideProgress()
      }

      // Generate context
      const convId = portal.state.currentConversationId
      if (convId) {
        fetch('/api/dashboard.php?action=generate_context', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + portal.getToken() },
          body: JSON.stringify({ group_id: convId }),
        }).then(r => r.json()).catch(() => {})
      }

      portal.state.currentRequestId = event.request_id || null
      resetInput()
      sidebarRef.value?.loadHistory()
    },
    onError: (msg) => {
      appendMessage('ai', sIcons.warn+' ' + msg, '系统提示', false, 'ai')
      resetInput()
    },
    onMediaDone: (event) => handleMediaDone(event),
    onStop: () => {
      appendMessage('ai', '⏹ 已停止生成', '系统提示', false, 'ai')
      resetInput()
    },
    onTokenWarning: (msg) => globalToast(msg, 'error'),
  })
}

function stopGeneration() {
  typewriterAborted = true
  portal.stopStreaming()
}

function resetInput() {
  inputText.value = ''
  inputDisabled.value = false
  autoResize()
  chatInputRef.value?.focus()
}

function showProgress(mode: string) {
  const overlay = document.getElementById('progress-overlay')
  if (overlay) overlay.classList.remove('hidden')
  if (isZenActive.value) {
    progressHTML.value = '<span class="pulse-dot"></span> 思考中…'
    return
  }
  if (portal.state.globalMode === 'debate') {
    // 深度思辨：显示 5 个进度圆点，全部等待中
    let dots = '<div class="debate-progress">'
    for (let i = 1; i <= 5; i++) dots += '<span class="prog-dot"></span>'
    dots += '<span class="debate-hint">准备进入深度思辨…</span>'
    dots += '<span class="debate-sub-hint">↻ 启动辩论引擎…</span></div>'
    progressHTML.value = dots
    return
  }
  const modeLabel = mode === 'expert'
    ? '<svg style="vertical-align:middle" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="8" r="7"/><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"/></svg> 专家模式'
    : '<svg style="vertical-align:middle" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg> 快速模式'
  progressHTML.value = `<span class="pulse-dot"></span>${modeLabel} · 正在解析问题，调度官分析中…`
}

function updateProgress(event: any) {
  const phase = event.phase || ''
  const message = event.message || ''
  const role = event.role || event.role_key || ''

  // message 事件：直接展示后端下发的状态文案
  if (event.type === 'message' && message) {
    progressHTML.value = `<span class="pulse-dot"></span>${escHtml(message)}`
    return
  }
  // start 事件：进入"调度中"状态
  if (event.type === 'start') {
    progressHTML.value = `<span class="pulse-dot"></span>调度中...`
    return
  }
  // done 事件：完成
  if (event.type === 'done') {
    progressHTML.value = '<span style="color:var(--success)">'+sIcons.check+' 完成</span>'
    setTimeout(hideProgress, 1500)
  }
}

function hideProgress() {
  // stopPhaseTimer / subHintInterval 仅在辩论模式的 sendMessage 闭包内定义，
  // 用 typeof 安全检测避免 ReferenceError
  if (typeof stopPhaseTimer === 'function') stopPhaseTimer()
  if (typeof subHintInterval !== 'undefined' && subHintInterval) { clearInterval(subHintInterval); subHintInterval = null }
  const overlay = document.getElementById('progress-overlay')
  if (overlay) overlay.classList.add('hidden')
}

function collectHistory() {
  const wrappers = chatMessagesRef.value?.querySelectorAll('.message-wrapper')
  if (!wrappers?.length) return []
  const history: any[] = []
  const MAX = isZenActive.value ? 8 : 6
  const items = Array.from(wrappers).slice(-MAX)
  for (const w of items) {
    const bubble = w.querySelector('.chat-bubble')
    if (!bubble) continue
    const text = bubble.textContent?.trim()
    if (!text) continue
    if (w.classList.contains('user')) {
      history.push({ role: 'user', content: text })
    } else if (w.classList.contains('ai') || w.classList.contains('summary')) {
      history.push({ role: 'assistant', content: text })
    }
  }
  return history
}

// ---- DOM Message Helpers ----
function appendMessage(type: string, text: string, roleLabel = '', isTypewriter = false, cssClass = '', createdAt = '') {
  const container = chatMessagesRef.value
  if (!container) return null

  const wrapper = document.createElement('div')
  wrapper.className = `message-wrapper ${type} ${cssClass}`.trim()

  // 存储 created_at 供 shareSingleBubble 使用
  if (createdAt) {
    wrapper.setAttribute('data-created-at', createdAt)
  }

  // 禅模式下跳过角色标签，保持一对一对话感
  if (roleLabel && !isZenActive.value) {
    const label = createRoleLabel(roleLabel, type)
    wrapper.appendChild(label)
  }

  const bubble = document.createElement('div')
  bubble.className = `chat-bubble ${type} ${cssClass}`.trim()

  bubble.innerHTML = isTypewriter ? text + '<span class="cursor-blink">|</span>' : text

  wrapper.appendChild(bubble)
  container.appendChild(wrapper)
  scrollToBottom()
  return bubble
}

// 获取当前用户显示名
function getDisplayName(): string {
  // 优先使用个人中心设置的 display_name
  const profileName = portal.state.userDisplayName
  if (profileName) return profileName
  try {
    const pu = localStorage.getItem('portal_user')
    if (pu) {
      const u = JSON.parse(pu)
      return u.username || u.client_name || '我'
    }
  } catch (_) {}
  return '我'
}

// 创建角色标签（带头像和名字）
function createRoleLabel(name: string, msgType: string): HTMLElement {
  const label = document.createElement('div')
  const labelType = msgType === 'user' ? 'user' : 'ai'
  label.className = `role-label ${labelType}`
  const displayName = msgType === 'user' ? getDisplayName() : name
  // 头像
  const avatar = document.createElement('span')
  avatar.className = 'role-avatar'
  // 用户消息：如果有个人中心头像图片就用图片，否则用首字母
  if (msgType === 'user' && portal.state.userAvatar) {
    const img = document.createElement('img')
    img.src = portal.state.userAvatar
    img.alt = displayName
    img.style.width = '100%'
    img.style.height = '100%'
    img.style.borderRadius = '50%'
    img.style.objectFit = 'cover'
    avatar.appendChild(img)
  } else {
    avatar.textContent = displayName.charAt(0)
  }
  label.appendChild(avatar)
  // 名字
  const nameSpan = document.createElement('span')
  nameSpan.className = 'role-name'
  nameSpan.textContent = displayName
  label.appendChild(nameSpan)
  return label
}

function addPhaseSeparator(text: string) {
  const container = chatMessagesRef.value
  if (!container) return
  const sep = document.createElement('div')
  sep.className = 'phase-separator'
  sep.innerHTML = `<span class="dot green"></span> ${text}`
  container.appendChild(sep)
  scrollToBottom()
}

function addDebatePhaseSeparator(label: string, cssClass: string, iconSvg: string) {
  const container = chatMessagesRef.value
  if (!container) return
  const sep = document.createElement('div')
  sep.className = `debate-phase-sep ${cssClass}`
  sep.innerHTML = `<span class="debate-phase-line"></span><span class="debate-phase-icon">${iconSvg}</span><span class="debate-phase-label">${label}</span><span class="debate-phase-line"></span>`
  container.appendChild(sep)
  scrollToBottom()
}

// ---- 右侧节点导航 ----
function updateNavDots() {
  const navContainer = document.getElementById('chat-nav-dots')
  if (!navContainer) return
  const area = document.getElementById('chat-messages')
  if (!area) return

  // 收集所有消息节点：用户消息、AI回复、辩论阶段分隔线
  const items: Array<{ el: HTMLElement, type: 'user' | 'ai' | 'phase', label: string, cssClass: string }> = []
  area.querySelectorAll('.message-wrapper, .debate-phase-sep').forEach(el => {
    const htmlEl = el as HTMLElement
    if (htmlEl.classList.contains('message-wrapper')) {
      if (htmlEl.classList.contains('user')) {
        const bubble = htmlEl.querySelector('.chat-bubble')
        const text = bubble?.textContent?.trim().slice(0, 15) || '提问'
        items.push({ el: htmlEl, type: 'user', label: text, cssClass: 'nav-user' })
      } else {
        // AI 回复消息：辩论模式下跳过（阶段分隔线已覆盖导航），普通对话则添加灰色圆点
        const isDebateMsg = htmlEl.classList.contains('debate-calibrator') || htmlEl.classList.contains('debate-left') || htmlEl.classList.contains('debate-right')
        if (!isDebateMsg) {
          const roleLabel = htmlEl.querySelector('.role-label .role-name')?.textContent?.trim()
          const label = roleLabel || 'AI'
          items.push({ el: htmlEl, type: 'ai', label: label, cssClass: 'nav-ai' })
        }
      }
    } else if (htmlEl.classList.contains('debate-phase-sep')) {
      const label = htmlEl.querySelector('.debate-phase-label')?.textContent?.trim() || ''
      let cssClass = ''
      if (htmlEl.classList.contains('debate-calibrator')) cssClass = 'nav-calibrator'
      else if (htmlEl.classList.contains('debate-left')) cssClass = 'nav-left'
      else if (htmlEl.classList.contains('debate-right')) cssClass = 'nav-right'
      items.push({ el: htmlEl, type: 'phase', label, cssClass })
    }
  })

  if (items.length === 0) {
    navContainer.classList.remove('visible')
    navContainer.innerHTML = ''
    return
  }
  navContainer.classList.add('visible')

  let html = ''
  items.forEach((item, i) => {
    const escapedLabel = item.label.replace(/"/g, '&quot;')
    html += `<button class="chat-nav-dot ${item.cssClass}" data-nav-idx="${i}" data-tooltip="${escapedLabel}" title="${escapedLabel}"></button>`
  })
  navContainer.innerHTML = html

  navContainer.querySelectorAll('.chat-nav-dot').forEach(dot => {
    dot.addEventListener('click', () => {
      const idx = parseInt((dot as HTMLElement).dataset.navIdx || '0')
      const item = items[idx]
      if (item) {
        item.el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        navContainer!.querySelectorAll('.chat-nav-dot').forEach(d => d.classList.remove('active'))
        dot.classList.add('active')
      }
    })
  })
}

function hideNavDots() {
  const navContainer = document.getElementById('chat-nav-dots')
  if (navContainer) { navContainer.classList.remove('visible'); navContainer.innerHTML = '' }
}
// ---- /右侧节点导航 ----

function scrollToBottom() {
  if (userScrolledUp) return
  const area = document.getElementById('chat-area')
  if (area) {
    requestAnimationFrame(() => {
      if (!userScrolledUp) area.scrollTop = area.scrollHeight
    })
  }
}

// ---- Typewriter ----
function typewrite(bubbleEl: HTMLElement, text: string, onComplete?: () => void) {
  if (!bubbleEl || !text) { onComplete?.(); return }
  const speed = CFG.TYPEWRITER_SPEED || 30
  let index = 0
  bubbleEl.classList.add('typewriting')
  function done() {
    bubbleEl.classList.remove('typewriting')
    bubbleEl.innerHTML = renderMarkdown(text)
    onComplete?.()
  }

  function tick() {
    if (typewriterAborted || tabHidden) { done(); return }
    if (index < text.length) {
      const partial = text.substring(0, index + 1)
      bubbleEl.innerHTML = renderPartialMarkdown(partial)
      index++
      scrollToBottom()
      setTimeout(tick, speed + Math.random() * 15)
    } else {
      done()
    }
  }
  tick()
}

// ---- Markdown ----
function escapeHtml(text: string) {
  return String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>')
}
function escHtml(s: any) { return escapeHtml(String(s)) }

function protectMathBlocks(text: string) {
  const blocks: any[] = []
  let idx = 0
  text = text.replace(/\\\[([\s\S]*?)\\\]/g, (_, math) => {
    blocks.push({ math: math.trim(), display: true })
    return '%%MATH_' + (idx++) + '%%'
  })
  text = text.replace(/\\\(([\s\S]*?)\\\)/g, (_, math) => {
    blocks.push({ math: math.trim(), display: false })
    return '%%MATH_' + (idx++) + '%%'
  })
  return { text, blocks }
}

function restoreMathBlocks(html: string, blocks: any[]) {
  if (typeof katex === 'undefined') {
    return html.replace(/%%MATH_(\d+)%%/g, (_, i) => {
      const b = blocks[parseInt(i)]
      return b ? escapeHtml(b.math) : ''
    })
  }
  return html.replace(/%%MATH_(\d+)%%/g, (_, i) => {
    const b = blocks[parseInt(i)]
    if (!b) return ''
    try {
      return katex.renderToString(b.math, { displayMode: b.display, throwOnError: false })
    } catch (e) {
      return '<code>' + escapeHtml(b.math) + '</code>'
    }
  })
}

function renderMarkdown(text: string): string {
  if (!text) return ''
  // 压缩连续3个以上换行为2个（一个空行），避免产生空 <p></p>
  let t = text.replace(/\n{3,}/g, '\n\n')
  if (typeof marked === 'undefined') return escapeHtml(t).replace(/\n/g, '<br>')
  const p = protectMathBlocks(t)
  const html = marked.parse(p.text, { breaks: false })
  const safeHtml = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
  const result = restoreMathBlocks(safeHtml, p.blocks)
  // ★ DOMPurify 净化，防止 XSS（事件处理器、javascript: 等）
  return typeof DOMPurify !== 'undefined' ? DOMPurify.sanitize(result, { ADD_ATTR: ['target'] }) : result
}

function renderPartialMarkdown(text: string): string {
  if (!text) return ''
  let t = text.replace(/\n{3,}/g, '\n\n')
  const fences = t.match(/```/g)
  if (fences && fences.length % 2 !== 0) {
    t = t.substring(0, t.lastIndexOf('```'))
  }
  try {
    const p = protectMathBlocks(t)
    let html = marked.parse(p.text, { breaks: false })
    html = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    html = html.replace(/<\/?p>/g, '')
    const result = restoreMathBlocks(html, p.blocks)
    // ★ DOMPurify 净化
    return typeof DOMPurify !== 'undefined' ? DOMPurify.sanitize(result, { ADD_ATTR: ['target'] }) : result
  } catch (e) {
    return escapeHtml(t).replace(/\n/g, '<br>')
  }
}

// ---- Time Format ----
function fmtTime(createdAt: string): string {
  if (!createdAt) return new Date().toLocaleString()
  // API returns "2026-06-05 06:37:49" format, convert to Date
  const d = new Date(createdAt.replace(' ', 'T') + '+08:00')
  if (isNaN(d.getTime())) return createdAt
  return d.getFullYear() + '-' +
    String(d.getMonth() + 1).padStart(2, '0') + '-' +
    String(d.getDate()).padStart(2, '0') + ' ' +
    String(d.getHours()).padStart(2, '0') + ':' +
    String(d.getMinutes()).padStart(2, '0') + ':' +
    String(d.getSeconds()).padStart(2, '0')
}

// ---- Bubble Actions ----
function addBubbleActions(bubbleEl: HTMLElement, roleKey: string, isCached = false, createdAt = '') {
  const wrapper = bubbleEl.parentElement
  if (!wrapper || wrapper.querySelector('.bubble-actions')) return

  // 存储 created_at 到 wrapper，供 shareSingleBubble 使用
  if (createdAt && wrapper) {
    wrapper.setAttribute('data-created-at', createdAt)
  }

  const actions = document.createElement('div')
  actions.className = 'bubble-actions'
  actions.style.setProperty('opacity', '1', 'important')
  actions.style.setProperty('display', 'flex', 'important')

  // Copy
  const copyBtn = document.createElement('button')
  copyBtn.className = 'bubble-btn bubble-btn-copy'
  copyBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg><span>复制</span>'
  copyBtn.title = '复制内容'
  copyBtn.addEventListener('click', () => {
    navigator.clipboard.writeText(bubbleEl.textContent || '')
    globalToast('已复制', 'success')
  })
  actions.appendChild(copyBtn)

  // Share
  const shareBtn = document.createElement('button')
  shareBtn.className = 'bubble-btn bubble-btn-share'
  shareBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg><span>分享</span>'
  shareBtn.title = '分享此条消息'
  shareBtn.addEventListener('click', () => {
    shareSingleBubble(wrapper)
  })
  actions.appendChild(shareBtn)

  // Like / Dislike
  const likeBtn = document.createElement('button')
  likeBtn.className = 'bubble-btn bubble-btn-like'
  likeBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3m7-2V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14Z"/></svg><span>有用</span>'
  likeBtn.title = '回答有帮助'
  likeBtn.addEventListener('click', () => submitFeedback(1, likeBtn, actions))

  const dislikeBtn = document.createElement('button')
  dislikeBtn.className = 'bubble-btn bubble-btn-dislike'
  dislikeBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 2h3a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-3m-7-2V5a3 3 0 0 1 3-3l4 9v11H6.72a2 2 0 0 1-2-1.7l-1.38-9a2 2 0 0 1 2-2.3H14Z"/></svg><span>没用</span>'
  dislikeBtn.title = '回答没有帮助'
  dislikeBtn.addEventListener('click', () => submitFeedback(0, dislikeBtn, actions))

  actions.appendChild(likeBtn)
  actions.appendChild(dislikeBtn)

  // Collapse
  const collapseBtn = document.createElement('button')
  collapseBtn.className = 'bubble-btn bubble-btn-collapse'
  collapseBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m18 15-6-6-6 6"/></svg>'
  collapseBtn.title = '折叠'
  collapseBtn.addEventListener('click', () => {
    const bubble = wrapper.querySelector('.chat-bubble') as HTMLElement
    if (!bubble) return
    const isCollapsed = bubble.classList.toggle('collapsed')
    collapseBtn.innerHTML = isCollapsed
      ? '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m6 9 6 6 6-6"/></svg>'
      : '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m18 15-6-6-6 6"/></svg>'
    collapseBtn.title = isCollapsed ? '展开' : '折叠'
  })
  actions.appendChild(collapseBtn)

  const time = document.createElement('div')
  time.className = 'bubble-time'
  time.textContent = fmtTime(createdAt)
  wrapper.appendChild(actions)
  wrapper.appendChild(time)

  // 文字类回答：添加行动按钮（生成配图 / 生成视频），放在分享按钮后面
  // 禅模式不显示生成配图/视频按钮
  const mediaKeys = ['text2img', 'img2img', 'video_gen', 'video', 'video_create', 'video_i2v']
  if (roleKey && roleKey !== 'summary' && roleKey !== 'zen_master' && !mediaKeys.includes(roleKey)) {
    addActionButtons(actions, bubbleEl, shareBtn)
  }

  // 媒体类回答：添加媒体行动按钮（分析图片 / 改风格 / 下载原图），放在分享按钮后面
  if (roleKey && mediaKeys.includes(roleKey)) {
    addMediaActions(actions, bubbleEl, shareBtn)
  }
}

// ---- 分享功能 ----
// 进入选择模式
function toggleShareSelect() {
  enterShareSelectMode()
}

// 进入分享选择模式（可指定预选索引和 created_at，用于单条气泡分享）
// singleCreatedAt: 单条分享时，被选中气泡的 created_at 时间戳（用于后端精确匹配）
function enterShareSelectMode(preSelected?: number[], singleCreatedAt?: string) {
  const area = chatMessagesRef.value
  if (!area) {
    console.warn('[share] chatMessagesRef is null, cannot toggle share select')
    globalToast('请先进行对话后再分享', 'error')
    return
  }

  shareSelectMode.value = true
  shareSelectedIndices.value = new Set()
  // 存储单条分享的 created_at（用于后端精确匹配）
  ;(window as any).__shareSingleCreatedAt = singleCreatedAt || null

  // 收集所有 message-wrapper
  shareAllWrappers = Array.from(area.querySelectorAll('.message-wrapper')) as HTMLElement[]

  const preSet = preSelected ? new Set(preSelected) : null
  const selectAll = !preSet  // 无预设时全选

  shareAllWrappers.forEach((w, i) => {
    // 跳过阶段分隔线
    if (w.classList.contains('debate-phase-sep')) return

    // 添加选择标记
    const cb = document.createElement('div')
    cb.className = 'share-check'
    cb.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>'
    cb.style.display = 'none'
    w.dataset.shareIndex = String(i)
    w.classList.add('share-selectable')
    w.insertBefore(cb, w.firstChild)

    if (selectAll || preSet!.has(i)) {
      shareSelectedIndices.value.add(i)
      cb.style.display = 'flex'
      w.classList.add('share-selected')
    }
  })

  // 使用事件委托处理点击
  area.addEventListener('click', onShareWrapperClick)
}

function onShareWrapperClick(e: MouseEvent) {
  if (!shareSelectMode.value) return
  const target = (e.target as HTMLElement).closest('.message-wrapper') as HTMLElement
  if (!target || !target.dataset.shareIndex) return
  const i = parseInt(target.dataset.shareIndex)
  const cb = target.querySelector('.share-check') as HTMLElement

  if (shareSelectedIndices.value.has(i)) {
    shareSelectedIndices.value.delete(i)
    if (cb) cb.style.display = 'none'
    target.classList.remove('share-selected')
  } else {
    shareSelectedIndices.value.add(i)
    if (cb) cb.style.display = 'flex'
    target.classList.add('share-selected')
  }
}

// 点击选择单条
function shareSingleBubble(wrapper: HTMLElement) {
  const area = chatMessagesRef.value
  if (!area) {
    globalToast('请先进行对话后再分享', 'error')
    return
  }
  // 读取气泡的 created_at 时间戳（精确匹配，避免 DOM 索引错位）
  const createdAt = wrapper.getAttribute('data-created-at') || ''
  if (!createdAt) {
    // 兜底：没有时间戳时回退到旧的全选模式
    console.warn('[share] wrapper has no data-created-at, falling back to select-all mode')
    enterShareSelectMode()
    return
  }
  // 找到 wrapper 在 DOM 中的索引（仅用于 UI 选中状态，不再用于后端匹配）
  const allWrappers = Array.from(area.querySelectorAll('.message-wrapper')) as HTMLElement[]
  const idx = allWrappers.indexOf(wrapper)
  if (idx < 0) {
    enterShareSelectMode()
    return
  }
  // 进入选择模式，仅预选该气泡（UI 用索引，后端用 created_at）
  enterShareSelectMode([idx], createdAt)
}

// 取消选择模式
function cancelShareSelect() {
  shareSelectMode.value = false
  shareSelectedIndices.value = new Set()
  shareModalOpen.value = false
  shareLink.value = ''

  // 移除事件监听
  const area = chatMessagesRef.value
  if (area) area.removeEventListener('click', onShareWrapperClick)

  shareAllWrappers.forEach(w => {
    w.classList.remove('share-selectable', 'share-selected')
    const cb = w.querySelector('.share-check')
    if (cb) cb.remove()
  })
  shareAllWrappers = []
}

// 创建分享链接
async function createShare() {
  if (shareSelectedIndices.value.size === 0) {
    globalToast('请至少选择一条消息', 'error')
    return
  }

  const requestId = portal.state.currentConversationId || portal.state.currentRequestId
  if (!requestId) {
    globalToast('请先发送对话后再生成分享链接', 'error')
    return
  }

  shareCreating.value = true
  try {
    const selectedIds = Array.from(shareSelectedIndices.value)
    const singleCreatedAt = (window as any).__shareSingleCreatedAt || null
    console.log('[share] creating share with requestId:', requestId, 'selectedIds:', selectedIds, 'singleCreatedAt:', singleCreatedAt)
    const payload: any = {
      request_id: requestId,
      selected_message_ids: selectedIds,
    }
    // 单条气泡分享：传 created_at 给后端做精确匹配
    if (singleCreatedAt) {
      payload.single_created_at = singleCreatedAt
    }
    const res = await portal.apiPost('share.php?action=create', payload)
    console.log('[share] api response:', res)
    if (res.share_id) {
      shareLink.value = res.url
      shareModalOpen.value = true
    } else {
      globalToast(res.error || '创建分享失败', 'error')
    }
  } catch (e: any) {
    console.error('[share] create error:', e)
    globalToast('创建分享失败: ' + (e.message || '网络错误'), 'error')
  } finally {
    shareCreating.value = false
  }
}

// 复制分享链接
function copyShareLink() {
  if (!shareLink.value) return
  navigator.clipboard.writeText(shareLink.value)
  shareModalOpen.value = false
  globalToast('复制完成', 'success', 1000)
}

// ---- 创作成果详情 ----
function onOpenCreativeDetail(item: any) {
  // 关闭侧边栏（移动端）
  if (mobileSidebarOpen.value) toggleMobileSidebar()
  // 保存对话快照（从画廊面板进来时已保存过，这里幂等操作不会覆盖）
  if (!hasSnapshot) saveChatSnapshot()
  // 清除旧气泡
  messages.length = 0
  const container = chatMessagesRef.value
  if (container) {
    container.querySelectorAll('.message-wrapper,.phase-separator').forEach((el: Element) => el.remove())
  }
  hideNavDots()
  // 隐藏输入区居中状态
  inputCentered.value = false
  inputEverSent = true
  creativeDetail.value = item
  rightPanel.value = 'detail'
  // 滚动到顶部
  nextTick(() => {
    const area = chatMessagesRef.value
    if (area) area.scrollTop = 0
  })
}

// 从画廊面板打开成果详情
function openGalleryDetail(item: any) {
  onOpenCreativeDetail(item)
}

// ---- 对话状态快照（面板切换时保存/恢复） ----
function saveChatSnapshot() {
  // 保存当前 requestId，用于返回时重新加载对话
  chatSnapshot.messages = [...messages]
  chatSnapshot.inputCentered = inputCentered.value
  chatSnapshot.inputEverSent = inputEverSent
  hasSnapshot = true
}

async function restoreChatSnapshot() {
  const requestId = portal.state.currentRequestId
  if (!hasSnapshot || !requestId) {
    // 没有快照或没有对话ID → 回到首页
    messages.length = 0
    const container = chatMessagesRef.value
    if (container) {
      container.querySelectorAll('.message-wrapper,.debate-phase-sep,.phase-separator').forEach((el: Element) => el.remove())
    }
    centerInputArea()
    rightPanel.value = 'chat'
    return
  }
  // 用 loadConversation 重新加载对话
  rightPanel.value = 'chat'
  await loadConversation(requestId)
}

// ---- 内容页面（用户协议/隐私政策/帮助与反馈） ----
function openContentPage(type: 'terms' | 'privacy' | 'help') {
  contentPageType.value = type
  showContentPage.value = true
}

// ---- 我的分享（右侧面板） ----
async function onOpenShares() {
  if (mobileSidebarOpen.value) toggleMobileSidebar()
  // 保存当前对话状态快照，返回时恢复
  saveChatSnapshot()
  // 清除旧气泡
  messages.length = 0
  const container = chatMessagesRef.value
  if (container) {
    container.querySelectorAll('.message-wrapper,.phase-separator').forEach((el: Element) => el.remove())
  }
  hideNavDots()
  inputCentered.value = false
  inputEverSent = true
  rightPanel.value = 'shares'
  // 加载分享列表
  shareListLoading.value = true
  try {
    const data = await portal.apiGet('share.php?action=list')
    myShares.value = data.shares || []
  } catch (_) {}
  shareListLoading.value = false
  // 滚动到顶部
  nextTick(() => {
    const area = chatMessagesRef.value
    if (area) area.scrollTop = 0
  })
}

async function handleCopyShareUrl(url: string) {
  await navigator.clipboard.writeText(url)
  globalToast('复制完成', 'success', 1000)
}

function handleOpenShareUrl(url: string) {
  window.open(url, '_blank')
}

async function handleDeleteShare(shareId: string) {
  try {
    await portal.apiPost('share.php?action=delete', { share_id: shareId })
    myShares.value = myShares.value.filter((s: any) => s.share_id !== shareId)
    globalToast('已删除', 'success')
  } catch (e: any) {
    globalToast('删除失败', 'error')
  }
}

// ---- 创作成果画廊（右侧面板） ----
async function onOpenGallery() {
  if (mobileSidebarOpen.value) toggleMobileSidebar()
  // 保存当前对话状态快照，返回时恢复
  saveChatSnapshot()
  // 清除旧气泡
  messages.length = 0
  const container = chatMessagesRef.value
  if (container) {
    container.querySelectorAll('.message-wrapper,.phase-separator').forEach((el: Element) => el.remove())
  }
  hideNavDots()
  inputCentered.value = false
  inputEverSent = true
  rightPanel.value = 'gallery'
  // 加载画廊数据
  galleryLoading.value = true
  try {
    const data = await portal.apiGet('dashboard.php', { action: 'creative_media', per_page: 30 })
    galleryItems.value = data.media || []
  } catch (_) {}
  galleryLoading.value = false
  // 滚动到顶部
  nextTick(() => {
    const area = chatMessagesRef.value
    if (area) area.scrollTop = 0
  })
}

function roleTypeLabel(roleKey: string): string {
  const map: Record<string, string> = {
    text2img: '文生图',
    img2img: '图生图',
    video: '视频生成',
    video_gen: '视频生成',
    video_i2v: '图生视频',
    video_create: '视频创作',
  }
  return map[roleKey] || roleKey
}

function openCreativeLightbox() {
  if (!creativeDetail.value) return
  ;(window as any).__openLightbox?.(creativeDetail.value.url)
}

function downloadCreativeMedia() {
  if (!creativeDetail.value) return
  const url = creativeDetail.value.url
  const a = document.createElement('a')
  a.href = url
  a.download = creativeDetail.value.prompt?.substring(0, 20) || 'creative_media'
  a.target = '_blank'
  a.click()
}

function shareCreativeMedia() {
  if (!creativeDetail.value) return
  const url = creativeDetail.value.url
  const text = `AI创作成果\n${creativeDetail.value.prompt || ''}\n${url}`
  if (navigator.clipboard) {
    navigator.clipboard.writeText(text).then(() => {
      globalToast('内容已复制到剪贴板', 'success')
    })
  } else {
    globalToast('请手动复制链接: ' + url, 'info')
  }
}

// 文字回答的行动按钮：生成配图 / 生成视频（放在分享按钮后面，和复制/分享同一行）
function addActionButtons(actions: HTMLElement, bubbleEl: HTMLElement, afterEl?: HTMLElement) {
  const imgBtn = document.createElement('button')
  imgBtn.className = 'bubble-btn'
  imgBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg><span>生成配图</span>'
  imgBtn.title = '根据此回答内容生成配图'
  imgBtn.addEventListener('click', () => {
    const text = bubbleEl?.textContent?.trim() || ''
    if (text) actionContinue('text2img', text)
  })

  const vidBtn = document.createElement('button')
  vidBtn.className = 'bubble-btn'
  vidBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="5 3 19 12 5 21 5 3"/></svg><span>生成视频</span>'
  vidBtn.title = '根据此回答内容生成视频'
  vidBtn.addEventListener('click', () => {
    const text = bubbleEl?.textContent?.trim() || ''
    if (text) actionContinue('video_gen', text)
  })

  // 插入到指定元素后面（默认插在 actions 末尾）
  if (afterEl) {
    afterEl.insertAdjacentElement('afterend', vidBtn)
    afterEl.insertAdjacentElement('afterend', imgBtn)
  } else {
    actions.appendChild(imgBtn)
    actions.appendChild(vidBtn)
  }
}

// 媒体回答的行动按钮：分析图片 / 改风格 / 图生视频 / 下载原图（和复制/分享同一行）
function addMediaActions(actions: HTMLElement, bubbleEl: HTMLElement, afterEl?: HTMLElement) {
  const wrapper = bubbleEl.parentElement
  const downloadUrl = wrapper?.dataset?.downloadUrl || ''
  // 原图 URL（优先用 wrapper 上的 downloadUrl，兜底用 img.src）
  const imgEl = bubbleEl?.querySelector('img') as HTMLImageElement | null
  const originalUrl = downloadUrl || imgEl?.src || ''

  const analyzeBtn = document.createElement('button')
  analyzeBtn.className = 'bubble-btn'
  analyzeBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg><span>分析图片</span>'
  analyzeBtn.title = '分析这张图片的配色和构图'
  analyzeBtn.addEventListener('click', () => {
    if (originalUrl) analyzeImage(originalUrl)
  })

  const restyleBtn = document.createElement('button')
  restyleBtn.className = 'bubble-btn'
  restyleBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg><span>改风格</span>'
  restyleBtn.title = '将这张图片改成其他风格（使用原图）'
  restyleBtn.addEventListener('click', () => {
    if (originalUrl) restyleImage(originalUrl)
  })

  // 图生视频按钮：加载原图到图生视频面板
  const i2vBtn = document.createElement('button')
  i2vBtn.className = 'bubble-btn'
  i2vBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><polygon points="17 10 12 15 7 10"/><line x1="12" y1="15" x2="12" y2="21"/></svg><span>图生视频</span>'
  i2vBtn.title = '用这张原图生成视频'
  i2vBtn.addEventListener('click', () => {
    if (originalUrl) restyleForVideo(originalUrl)
  })

  // 下载原图按钮
  let downloadBtn: HTMLElement | null = null
  if (downloadUrl) {
    downloadBtn = document.createElement('button')
    downloadBtn.className = 'bubble-btn'
    downloadBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg><span>下载原图</span>'
    downloadBtn.title = '下载原始图片'
    downloadBtn.addEventListener('click', () => window.open(downloadUrl, '_blank'))
  }

  // 插入到指定元素后面
  if (afterEl) {
    if (downloadBtn) afterEl.insertAdjacentElement('afterend', downloadBtn)
    afterEl.insertAdjacentElement('afterend', i2vBtn)
    afterEl.insertAdjacentElement('afterend', restyleBtn)
    afterEl.insertAdjacentElement('afterend', analyzeBtn)
  } else {
    actions.appendChild(analyzeBtn)
    actions.appendChild(restyleBtn)
    actions.appendChild(i2vBtn)
    if (downloadBtn) actions.appendChild(downloadBtn)
  }
}

// 从文字气泡跳转到AI创作面板
function actionContinue(targetRole: string, sourceText: string) {
  if (!portal.isLoggedIn.value) {
    showLoginModalFn('请登录后使用此功能', '请登录')
    return
  }
  setGlobalMode('create')
  let prompt = sourceText.substring(0, 300)
  prompt = prompt.replace(/[#*`>\[\]()]/g, '').trim()
  const tabMap: Record<string, string> = { text2img: 'text2img', video_gen: 'video' }
  const targetTab = tabMap[targetRole] || targetRole
  switchCreateTab(targetTab)
  setTimeout(() => {
    createPrompt.value = prompt
  }, 100)
  globalToast('已切换到AI创作面板，可修改提示词后生成', 'info')
}

// 分析图片：将图片加入对话框并预设提问
function analyzeImage(imgUrl: string) {
  if (!portal.isLoggedIn.value) {
    showLoginModalFn('请登录后使用此功能', '请登录')
    return
  }
  setGlobalMode('chat')
  pendingFiles.push({ url: imgUrl, type: 'image', name: '生成图片' })
  renderFilePreviewsDOM()
  inputText.value = '请分析这张图片的配色方案、构图特点和视觉风格'
  const input = document.getElementById('chat-input') as HTMLTextAreaElement | null
  if (input) input.focus()
  globalToast('图片已添加，输入问题后发送即可分析', 'info')
}

// 改风格：加载图片到图生图面板
async function restyleImage(imgUrl: string) {
  if (!portal.isLoggedIn.value) {
    showLoginModalFn('请登录后使用此功能', '请登录')
    return
  }
  setGlobalMode('create')
  switchCreateTab('img2img')
  try {
    const resp = await fetch(imgUrl)
    const blob = await resp.blob()
    const file = new File([blob], 'restyle_image.png', { type: blob.type || 'image/png' })
    i2iFile.value = file
    const reader = new FileReader()
    reader.onload = function (e) {
      const preview = document.getElementById('i2i-preview') as HTMLImageElement
      const placeholder = document.getElementById('i2i-placeholder')
      const uploadArea = document.getElementById('upload-area')
      if (preview) { preview.src = e.target?.result as string; preview.classList.remove('hidden') }
      if (placeholder) placeholder.style.display = 'none'
      if (uploadArea) uploadArea.classList.add('has-image')
    }
    reader.readAsDataURL(file)
    globalToast('图片已加载，可修改提示词后生成新风格', 'info')
  } catch (e: any) {
    globalToast('加载图片失败: ' + (e.message || '请手动上传'), 'warning')
  }
}

// 图生视频：加载原图到图生视频面板
async function restyleForVideo(imgUrl: string) {
  if (!portal.isLoggedIn.value) {
    showLoginModalFn('请登录后使用此功能', '请登录')
    return
  }
  setGlobalMode('create')
  switchCreateTab('video_i2v')
  try {
    const resp = await fetch(imgUrl)
    const blob = await resp.blob()
    const file = new File([blob], 'i2v_image.png', { type: blob.type || 'image/png' })
    i2iFile.value = file
    const reader = new FileReader()
    reader.onload = function (e) {
      const preview = document.getElementById('i2i-preview') as HTMLImageElement
      const placeholder = document.getElementById('i2i-placeholder')
      const uploadArea = document.getElementById('upload-area')
      if (preview) { preview.src = e.target?.result as string; preview.classList.remove('hidden') }
      if (placeholder) placeholder.style.display = 'none'
      if (uploadArea) uploadArea.classList.add('has-image')
    }
    reader.readAsDataURL(file)
    globalToast('图片已加载到图生视频，输入提示词后即可生成', 'info')
  } catch (e: any) {
    globalToast('加载图片失败: ' + (e.message || '请手动上传'), 'warning')
  }
}

async function submitFeedback(value: number, clickedBtn: HTMLButtonElement, actionsContainer: HTMLElement) {
  const requestId = portal.state.currentRequestId
  if (!requestId) { globalToast('无法提交反馈', 'error'); return }
  try {
    await portal.apiPost('dashboard.php?action=feedback', { request_id: requestId, feedback: value })
    const buttons = actionsContainer.querySelectorAll('.bubble-btn-like, .bubble-btn-dislike')
    buttons.forEach(b => b.classList.remove('active'))
    clickedBtn.classList.add('active')
    feedbackCache[requestId] = value
    globalToast('反馈提交成功', 'success')
  } catch (e: any) {
    globalToast('反馈失败: ' + e.message, 'error')
  }
}

// ---- Confirm Buttons (multimodal) ----
// ---- Confirm Buttons (multimodal) ----
function showConfirmButtons(event: any) {
  const simpleAnswer = event.simple_answer || event.combined || ''
  // 保存多模态分析结果，供后续【请专家详细分析】使用
  pendingMultimodalAnswer = simpleAnswer
  if (simpleAnswer) {
    const bubble = appendMessage('ai', '', '多模态感知', false, 'ai markdown') as HTMLElement
    if (bubble) {
      typewrite(bubble, simpleAnswer, () => {
        const actions = document.createElement('div')
        actions.className = 'confirm-actions'
        actions.innerHTML =
          '<button class="btn-confirm-yes" onclick="window.__confirmMultimodal(\'yes\')">是，请专家分析</button>' +
          '<button class="btn-confirm-no" onclick="window.__confirmMultimodal(\'no\')">不用了</button>'
        bubble.appendChild(actions)
        scrollToBottom()
      })
    }
  }
}

// 多模态确认函数
async function confirmMultimodal(action: string) {
  if (portal.state.isStreaming) return
  typewriterAborted = false
  tabHidden = false
  userScrolledUp = false

  // 将多模态分析结果包装为可调度指令，让调度官知道需要派发专家做深入分析
  const message = '请基于以下多模态感知结果，调度合适的专家角色进行深入分析和多维度解读：\n\n' + (pendingMultimodalAnswer || '请分析')
  pendingMultimodalAnswer = ''

  // 捕获当前对话历史（清空前）
  const capturedHistory = collectHistory()

  // 新建对话：清空消息、DOM、创建新 conversation_id
  messages.length = 0
  pendingConfirmMessage = ''
  pendingConfirmFiles = []
  portal.newConversationId()
  sidebarRef.value?.clearCurrentConv()
  const container = chatMessagesRef.value
  if (container) container.querySelectorAll('.message-wrapper,.phase-separator').forEach((el: Element) => el.remove())

  inputDisabled.value = true

  const body: any = {
    message: message,
    confirm_action: action,
    conversation_id: portal.getOrCreateConversationId(),
    mode: 'dispatcher',
    gateway_mode: portal.state.gatewayMode || 'fast',
    history: capturedHistory,
  }
  if (currentConfirmRequestId) body.request_id = currentConfirmRequestId
  const reqId = currentConfirmRequestId  // 暂存
  currentConfirmRequestId = null

  showProgress(portal.state.gatewayMode)
  const requestRoles = new Map<string, any>()

  portal.createSSEConnection(body, {
    onStart: (event) => updateProgress(event),
    onMessage: (event) => updateProgress(event),
    onDispatcherResult: (event) => {
      const roles = event.parsed_roles || []
      progressHTML.value = `<span class="pulse-dot"></span>已识别 ${roles.length} 个分析角度` +
        (event.brief ? ` · ${escHtml(event.brief)}` : '') +
        `<br><small>${roles.map((r: any) => '· ' + (r.role_name || r.role)).join(' ')}</small>`
    },
    onRoleDone: (event) => {
      const roleKey = event.role_key || event.role || 'unknown'
      const response = event.response || event.role_response || event.content || ''
      const annotatedContent = event.annotated_content || response
      const roleName = event.role_name || roleKey
      requestRoles.set(roleKey, { response, annotatedContent, roleName })
      roleNameKeyMap.set(roleName, roleKey)
      // 第一个角色回答完成时，创建联动总结占位气泡
      if (requestRoles.size === 1) {
        addPhaseSeparator('联动总结')
        const totalRoles = (window as any).__totalDispatchedRoles || 0
        const estimatedTotal = totalRoles > 0 ? totalRoles : requestRoles.size + 1
        const summaryPlaceholder = appendMessage('ai',
          '<div class="summary-placeholder">'+sIcons.spin+' 正在收集专家意见...<span class="summary-progress">1/'+estimatedTotal+'</span></div>',
          '联动总结', false, 'summary markdown') as HTMLElement
        if (summaryPlaceholder) {
          summaryPlaceholder.setAttribute('data-summary-placeholder', '1')
        }
      }
      updateSummaryPlaceholder(requestRoles.size)
      const bubbleEl = appendMessage('ai', '', roleName, false, 'ai markdown') as HTMLElement
      if (bubbleEl) {
        addBubbleActions(bubbleEl, roleKey)
        collapseBubble(bubbleEl)
        typewrite(bubbleEl, response, () => {
          renderAnnotatedBubble(bubbleEl, annotatedContent, roleKey)
          updateCollapseBtn(bubbleEl, true)
        })
      }
      updateProgress(event)
    },
    onDone: (event) => {
      hideProgress()
      if (event.status === 'user_rejected') {
        currentConfirmRequestId = null
        resetInput()
        sidebarRef.value?.loadHistory()
        return
      }
      const summaryText = event.summary || ''
      if (summaryText && !event.single_role && requestRoles.size > 1) {
        updateSummaryPlaceholderToGenerating()
        const summaryBubble = findSummaryPlaceholderBubble()
        if (summaryBubble) {
          summaryBubble.setAttribute('data-summary-placeholder', '')
          typewrite(summaryBubble, summaryText, () => {
            renderSummaryRefLinks(summaryBubble)
            addBubbleActions(summaryBubble, 'summary')
          })
        } else {
          addPhaseSeparator('联动总结')
          const bubbleEl = appendMessage('ai', '', '联动总结', false, 'summary markdown') as HTMLElement
          if (bubbleEl) {
            typewrite(bubbleEl, summaryText, () => {
              renderSummaryRefLinks(bubbleEl)
              addBubbleActions(bubbleEl, 'summary')
            })
          }
        }
      }
      if (event.single_role) {
        addPhaseSeparator('单角色分析')
      }
      const convId = portal.state.currentConversationId
      if (convId) {
        fetch('/api/dashboard.php?action=generate_context', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + portal.getToken() },
          body: JSON.stringify({ group_id: convId }),
        }).then(r => r.json()).catch(() => {})
      }
      portal.state.currentRequestId = event.request_id || null
      resetInput()
      sidebarRef.value?.loadHistory()
      // 自动切换到新对话
      sidebarRef.value.currentConvId = portal.state.currentConversationId
    },
    onError: (msg) => {
      appendMessage('ai', sIcons.warn+' ' + msg, '系统提示', false, 'ai')
      resetInput()
    },
    onMediaDone: (event) => handleMediaDone(event),
    onStop: () => {
      appendMessage('ai', '<span class="s-stop-icon">■</span> 已停止生成', '系统提示', false, 'ai')
      resetInput()
    },
    onTokenWarning: (msg) => globalToast(msg, 'error'),
  })
}

// ---- Media Done Handler ----
function handleMediaDone(event: any) {
  const roleKey = event.role || 'text2img'
  const roleName = event.role_name || CFG.ASSISTANT_NAME
  const displayUrl = event.display_url || event.local_url || event.result?.display_url || event.result?.local_url || event.result?.url || ''
  const downloadUrl = event.download_url || event.result?.download_url || displayUrl
  const isVideo = event.media_type === 'video' || event.media_type === 'video_task' || roleKey === 'video_gen'

  const wrapper = document.createElement('div')
  wrapper.className = 'message-wrapper ai media'
  wrapper.dataset.roleKey = roleKey

  wrapper.appendChild(createRoleLabel(roleName, 'ai'))

  const bubble = document.createElement('div')
  bubble.className = 'chat-bubble ai media-bubble'
  Object.assign(bubble.style, { width: '100%', maxWidth: 'none', boxSizing: 'border-box', alignSelf: 'stretch', textAlign: 'center', whiteSpace: 'normal' })

  if (isVideo && displayUrl) {
    bubble.innerHTML = `<div class="media-card">
      <video controls src="${escHtml(displayUrl)}" style="max-width:100%;max-height:360px;border-radius:var(--radius)"></video>
      <div class="media-card-actions"><button class="media-card-btn" onclick="window.open('${escHtml(downloadUrl)}','_blank')">${sIcons.dl} 下载</button></div>
    </div>`
  } else if (isVideo && event.task_id) {
    bubble.innerHTML = `<div class="media-card" style="padding:16px">
      <div style="display:flex;align-items:center;gap:8px;color:var(--text-secondary)">
        <span class="spinner" style="width:16px;height:16px;border:2px solid var(--border-color);border-top-color:var(--accent);border-radius:50%;animation:spin 0.8s linear infinite;display:inline-block"></span>
        <span>视频生成中… 任务ID: ${escHtml(String(event.task_id).substring(0, 8))}…</span>
      </div>
    </div>`
  } else if (displayUrl) {
    bubble.innerHTML = `<div class="media-card">
      <img src="${escHtml(displayUrl)}" alt="AI生成" onclick="window.__openLightbox?.('${escHtml(displayUrl)}')" style="max-width:200px;max-height:200px;border-radius:8px;object-fit:cover;display:block;margin:0 auto">
    </div>`
    wrapper.dataset.downloadUrl = downloadUrl
  } else { return }

  wrapper.appendChild(bubble)
  const container = chatMessagesRef.value
  if (container) {
    container.appendChild(wrapper)
    addBubbleActions(bubble, roleKey, false)
    scrollToBottom()
  }
}

// ---- File Upload ----
function addFilePreviewToWrapper(wrapper: HTMLElement, files: any[]) {
  const imgFiles = files.filter(f => f.type === 'image')
  const docFiles = files.filter(f => f.type !== 'image')
  if (imgFiles.length > 0) {
    const imgRow = document.createElement('div')
    imgRow.className = 'user-msg-images'
    imgFiles.forEach(f => {
      const img = document.createElement('img')
      img.src = f.url; img.alt = f.name || ''; img.className = 'user-msg-thumb'
      img.title = f.name || ''
      img.onclick = (e) => { e.stopPropagation(); openLightbox(f.url) }
      imgRow.appendChild(img)
    })
    wrapper.appendChild(imgRow)
  }
  if (docFiles.length > 0) {
    const docRow = document.createElement('div')
    docRow.className = 'user-msg-docs'
    docFiles.forEach(f => {
      const span = document.createElement('span')
      span.className = 'user-msg-doc-tag'
      span.innerHTML = sIcons.clip+' ' + escHtml(f.name || '文件')
      span.title = f.name || ''
      span.onclick = () => window.open(f.url, '_blank')
      docRow.appendChild(span)
    })
    wrapper.appendChild(docRow)
  }
}

function onPaste(e: ClipboardEvent) {
  const items = e.clipboardData?.items
  if (!items) return
  for (const item of items) {
    if (item.type.startsWith('image/')) {
      e.preventDefault()
      const blob = item.getAsFile()
      if (blob) uploadFile(blob)
    }
  }
}

function onDrop(e: DragEvent) {
  e.preventDefault()
  e.stopPropagation()
  const files = e.dataTransfer?.files
  if (!files) return
  for (const file of files) uploadFile(file)
}

function toggleUploadMenu() {
  document.getElementById('upload-menu')?.classList.toggle('hidden')
}

function openFilePicker(type: string) {
  document.getElementById('upload-menu')?.classList.add('hidden')
  const input = document.createElement('input')
  input.type = 'file'
  if (type === 'image') input.accept = 'image/jpeg,image/png,image/gif,image/webp'
  else input.accept = '.pdf,.doc,.docx,.txt,.xls,.xlsx'
  input.multiple = true
  input.onchange = (e: any) => {
    for (const file of e.target.files) uploadFile(file)
  }
  input.click()
}

async function uploadFile(file: File) {
  if (pendingFiles.length >= 5) { globalToast('最多上传5个文件', 'warning'); return }
  if (file.size > 5 * 1024 * 1024) { globalToast('文件超过5MB限制', 'error'); return }

  const formData = new FormData()
  formData.append('file', file)
  try {
    const token = portal.getToken()
    const resp = await fetch('/api/upload.php', {
      method: 'POST',
      headers: { Authorization: 'Bearer ' + token },
      body: formData,
    })
    const data = await resp.json()
    if (data.success && data.data) {
      pendingFiles.push(data.data)
      renderFilePreviewsDOM()
      globalToast('已添加: ' + file.name, 'success')
    } else {
      globalToast('上传失败: ' + (data.error || '未知错误'), 'error')
    }
  } catch (e: any) {
    globalToast('上传失败: ' + e.message, 'error')
  }
}

function removeFile(index: number) {
  pendingFiles.splice(index, 1)
  renderFilePreviewsDOM()
}

function renderFilePreviewsDOM() {
  const bar = document.getElementById('file-preview-bar')
  if (!bar) return
  if (pendingFiles.length === 0) {
    bar.classList.add('hidden')
    bar.innerHTML = ''
    return
  }
  bar.classList.remove('hidden')
  bar.innerHTML = pendingFiles.map((f: any, i: number) => {
    const isImg = f.type === 'image'
    const icon = isImg ? sIcons.img : (f.type === 'pdf' ? sIcons.doc : sIcons.clip)
    const preview = isImg
      ? `<img src="${escHtml(f.url)}" alt="${escHtml(f.name)}" class="file-thumb" onclick="event.stopPropagation();window.__openLightbox?.('${escHtml(f.url)}')">`
      : `<span class="file-icon">${icon}</span>`
    return `<div class="file-preview-item" title="${escHtml(f.name)}">
      ${preview}
      <span class="file-preview-name">${escHtml((f.name || '').substring(0, 15))}</span>
      <button class="file-preview-remove" onclick="window.__removeFile(${i})">×</button>
    </div>`
  }).join('')
}

// ---- Mention ----
function insertAtSymbol() {
  const input = chatInputRef.value
  if (!input) return
  input.focus()
  const start = input.selectionStart
  const end = input.selectionEnd
  const before = input.value.substring(0, start)
  const after = input.value.substring(end)
  inputInputTextBypass(before + '@' + after, start + 1)
  handleMentionInput({} as any)
}

function inputInputTextBypass(value: string, cursorPos: number) {
  const input = chatInputRef.value
  if (!input) return
  input.value = value
  inputText.value = value
  input.setSelectionRange(cursorPos, cursorPos)
  input.dispatchEvent(new Event('input', { bubbles: true }))
}

function handleMentionInput(e: Event) {
  const input = chatInputRef.value
  if (!input) return
  const text = input.value
  const cursorPos = input.selectionStart || text.length
  const textBefore = text.substring(0, cursorPos)
  const atMatch = textBefore.match(/@([\w\u4e00-\u9fff]*)$/)

  if (atMatch) {
    const query = atMatch[1]
    showMentionDropdown = true
    if (mentionSearchTimer) clearTimeout(mentionSearchTimer)
    mentionSearchTimer = setTimeout(() => searchRoles(query), 150)
  } else {
    showMentionDropdown = false
    hideMentionDropdown()
  }
}

async function searchRoles(query: string) {
  try {
    const token = portal.getToken()
    const resp = await fetch('/api/roles.php?action=search&q=' + encodeURIComponent(query), {
      headers: { Authorization: 'Bearer ' + token },
    })
    const data = await resp.json()
    if (data.success) {
      mentionSearchResults = data.data || []
      renderMentionDropdown()
    }
  } catch (e) {
    mentionSearchResults = []
  }
}

function renderMentionDropdown() {
  const existing = document.getElementById('mention-dropdown')
  if (existing) existing.remove()
  if (!showMentionDropdown || mentionSearchResults.length === 0) return

  const input = chatInputRef.value
  if (!input) return

  const dropdown = document.createElement('div')
  dropdown.id = 'mention-dropdown'
  dropdown.className = 'mention-dropdown'
  dropdown.innerHTML = mentionSearchResults.map((r: any) =>
    `<div class="mention-item" onclick="window.__selectMention('${escHtml(r.role_key)}','${escHtml(r.role_name)}')">
      <span class="mention-item-name">${escHtml(r.role_name)}</span>
      <span class="mention-item-key">${escHtml(r.role_key)}</span>
    </div>`
  ).join('') + '<div class="mention-dropdown-hint">↑↓ 选择  Enter 确认  Esc 关闭</div>'
  document.body.appendChild(dropdown)

  const rect = input.getBoundingClientRect()
  dropdown.style.left = rect.left + 'px'
  dropdown.style.top = (rect.top - dropdown.offsetHeight - 8) + 'px'
  dropdown.style.width = Math.min(rect.width, 320) + 'px'
}

function hideMentionDropdown() {
  document.getElementById('mention-dropdown')?.remove()
}

function selectMentionRole(roleKey: string, roleName: string) {
  if (mentionedRoles.some(r => r.role_key === roleKey)) return
  mentionedRoles.push({ role_key: roleKey, role_name: roleName })
  updateMentionChipsDOM()
  hideMentionDropdown()

  const input = chatInputRef.value
  if (input) {
    const text = input.value
    const cursorPos = input.selectionStart || text.length
    const before = text.substring(0, cursorPos)
    const after = text.substring(cursorPos)
    const newBefore = before.replace(/@[\w\u4e00-\u9fff]*$/, '')
    inputText.value = newBefore + after
    input.focus()
  }
}

function removeMentionRole(roleKey: string) {
  mentionedRoles = mentionedRoles.filter(r => r.role_key !== roleKey)
  updateMentionChipsDOM()
}

function updateMentionChipsDOM() {
  const container = document.getElementById('mention-chips')
  if (!container) return
  if (mentionedRoles.length === 0) {
    container.classList.add('hidden')
    container.innerHTML = ''
    return
  }
  container.classList.remove('hidden')
  container.innerHTML = mentionedRoles.map(r =>
    `<span class="mention-chip">@${escHtml(r.role_name)}<button class="mention-chip-remove" onclick="window.__removeMention('${escHtml(r.role_key)}')">×</button></span>`
  ).join('')
}

// ---- Lightbox ----
function openLightbox(url: string) {
  lightboxUrl.value = url
  showLightbox.value = true
}

// ---- Create Panel ----
const CREATE_CONFIG: Record<string, any> = {
  text2img: {
    hint: '输入画面描述，AI 为您生成图片',
    placeholder: '例如：一只橘猫趴在窗台上，午后阳光透过窗帘洒下，温暖写实风格...',
    btnText: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg> 生成图片',
    showUpload: false, showSize: true, showVideo: false,
  },
  img2img: {
    hint: '上传一张图片，描述您想要的风格变化',
    placeholder: '例如：将这张图改成赛博朋克风格...',
    btnText: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg> 转换风格',
    showUpload: true, showSize: true, showVideo: false,
  },
  video: {
    hint: '输入视频描述，AI 为您生成视频（异步任务，预计1-5分钟）',
    placeholder: '例如：一只猫在海滩散步，海浪轻拍，金色夕阳，电影质感...',
    btnText: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="5 3 19 12 5 21 5 3"/></svg> 生成视频',
    showUpload: false, showSize: false, showVideo: true, videoType: 't2v',
  },
  video_i2v: {
    hint: '上传一张图片，描述您期望的视频效果（异步任务，预计1-5分钟）',
    placeholder: '例如：让人物缓缓转头微笑，背景花瓣飘落...',
    btnText: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="7" width="20" height="13" rx="2" ry="2"/><polyline points="17 2 12 7 7 2"/></svg> 生成视频',
    showUpload: true, showSize: false, showVideo: true, videoType: 'i2v',
  },
}

function switchCreateTab(tab: string) {
  if (!CREATE_CONFIG[tab]) return
  createTab.value = tab
  const cfg = CREATE_CONFIG[tab]
  createHint.value = cfg.hint
  createPlaceholder.value = cfg.placeholder
  createBtnText.value = cfg.btnText

  toggleEl('upload-area', cfg.showUpload)
  toggleEl('size-row', cfg.showSize)
  toggleEl('video-params-row', cfg.showVideo)
  currentGroupId = null

  // Reset result & clear old chat history
  const resultEl = document.getElementById('create-result')
  if (resultEl) resultEl.classList.remove('active')
  const chatHistory = document.getElementById('create-chat-history')
  if (chatHistory) chatHistory.innerHTML = ''
  const promptEl = document.getElementById('create-prompt') as HTMLTextAreaElement
  if (promptEl) promptEl.value = ''
}

function toggleEl(id: string, show: boolean) {
  const el = document.getElementById(id)
  if (!el) return
  if (show) el.classList.remove('hidden')
  else el.classList.add('hidden')
}

function triggerImageUpload() {
  if (!portal.isLoggedIn.value) { showLoginModalFn('请登录后使用 AI 创作功能', '请登录'); return }
  i2iFileInput.value?.click()
}

function previewImage(event: Event) {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (!file) return
  if (!file.type.startsWith('image/')) { globalToast('请选择图片文件', 'warning'); return }
  i2iFile.value = file
  const reader = new FileReader()
  reader.onload = function (e) {
    const preview = document.getElementById('i2i-preview') as HTMLImageElement
    const placeholder = document.getElementById('i2i-placeholder')
    const uploadArea = document.getElementById('upload-area')
    if (preview) { preview.src = e.target?.result as string; preview.classList.remove('hidden') }
    if (placeholder) placeholder.style.display = 'none'
    if (uploadArea) uploadArea.classList.add('has-image')
  }
  reader.readAsDataURL(file)
}

async function doCreate() {
  if (!portal.isLoggedIn.value) { showLoginModalFn('请登录后使用 AI 创作功能', '请登录'); return }

  const prompt = createPrompt.value.trim()
  const cfg = CREATE_CONFIG[createTab.value]
  const resultEl = document.getElementById('create-result')

  if ((createTab.value === 'text2img' || createTab.value === 'video' || createTab.value === 'video_i2v') && !prompt) {
    globalToast('请输入描述', 'warning'); createPromptRef.value?.focus(); return
  }
  if ((createTab.value === 'img2img' || createTab.value === 'video_i2v') && !i2iFile.value) {
    globalToast('请先上传图片', 'warning'); return
  }

  creating.value = true
  if (resultEl) { resultEl.classList.add('active'); resultEl.innerHTML = '<div class="result-loading"><span class="spinner"></span>AI正在创作中，请稍候…</div>' }

  // ★ 立即追加用户消息到聊天区，不等API返回
  if (prompt) {
    appendCreateToChat(prompt, '', '', createTab.value)
  }
  // img2img 即使没文字也要显示用户消息
  if (createTab.value === 'img2img' && !prompt) {
    appendCreateToChat('风格转换', '', '', createTab.value)
  }
  createPrompt.value = ''

  try {
    const token = portal.getToken()
    const apiVersion = CFG.API_VERSION || '20260604'
    const baseUrl = '/api/media_proxy.php?v=' + apiVersion

    if (createTab.value === 'text2img') {
      const size = createSize.value
      const body: any = { action: 'text2img', prompt, size }
      if (currentGroupId) body.group_id = currentGroupId
      const resp = await fetch(baseUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify(body),
      })
      const data = await resp.json()
      if (data.success) {
        const displayUrl = data.data.display_url || data.data.local_url || data.data.url || ''
        const downloadUrl = data.data.download_url || data.data.local_url || data.data.url || ''
        if (resultEl) { resultEl.innerHTML = ''; resultEl.classList.remove('active') }
        // ★ 只追加AI结果（用户消息已先上屏）
        appendCreateToChat('', buildThumbHTML(displayUrl), downloadUrl, 'text2img')
        sidebarRef.value?.loadHistory()
      } else {
        throw new Error(data.error || '生成失败')
      }
    } else if (createTab.value === 'img2img') {
      const size = createSize.value
      const formData = new FormData()
      formData.append('action', 'img2img')
      formData.append('prompt', prompt || '')
      if (i2iFile.value) formData.append('image', i2iFile.value)
      formData.append('size', size)
      if (currentGroupId) formData.append('group_id', currentGroupId)
      const resp = await fetch(baseUrl, {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + token },
        body: formData,
      })
      const data = await resp.json()
      if (data.success) {
        const displayUrl = data.data.display_url || data.data.local_url || data.data.url || ''
        const downloadUrl = data.data.download_url || data.data.local_url || data.data.url || ''
        if (resultEl) { resultEl.innerHTML = ''; resultEl.classList.remove('active') }
        // ★ 只追加AI结果（用户消息已先上屏）
        appendCreateToChat('', buildThumbHTML(displayUrl), downloadUrl, 'img2img')
        sidebarRef.value?.loadHistory()
        i2iFile.value = null
        resetUploadUI()
      } else {
        throw new Error(data.error || '生成失败')
      }
    } else if (createTab.value === 'video') {
      const body = {
        action: 'video_create',
        model: videoModel.value,
        prompt,
        resolution: videoResolution.value,
        ratio: videoRatio.value,
        duration: parseInt(videoDuration.value),
        ...(currentGroupId ? { group_id: currentGroupId } : {}),
      }
      const resp = await fetch(baseUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify(body),
      })
      const data = await resp.json()
      if (data.success && data.data?.task_id) {
        videoTaskId = data.data.task_id
        startVideoPoll(resultEl, prompt)
      } else {
        throw new Error(data.error || '创建视频任务失败')
      }
    } else if (createTab.value === 'video_i2v') {
      const formData = new FormData()
      formData.append('action', 'video_create')
      formData.append('prompt', prompt || '')
      if (i2iFile.value) formData.append('image', i2iFile.value)
      formData.append('model', videoI2vModel.value)
      formData.append('resolution', videoResolution.value)
      formData.append('ratio', videoRatio.value)
      formData.append('duration', videoDuration.value)
      if (currentGroupId) formData.append('group_id', currentGroupId)
      const resp = await fetch(baseUrl, {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + token },
        body: formData,
      })
      const data = await resp.json()
      if (data.success && data.data?.task_id) {
        videoTaskId = data.data.task_id
        i2iFile.value = null
        resetUploadUI()
        startVideoPoll(resultEl, prompt)
      } else {
        throw new Error(data.error || '创建视频任务失败')
      }
    }
  } catch (e: any) {
    if (resultEl) resultEl.innerHTML = '<div style="color:#ef4444;padding:10px;">'+sIcons.warn+' '+escHtml(e.message)+'</div>'
    globalToast('生成失败: ' + e.message, 'error')
  } finally {
    if (createTab.value !== 'video' && createTab.value !== 'video_i2v') {
      creating.value = false
    }
  }
}

function buildThumbHTML(displayUrl: string) {
  return `<div class="media-result-thumb" style="text-align:center">
    <img src="${escHtml(displayUrl)}" alt="AI生成" onclick="window.__openLightbox?.('${escHtml(displayUrl)}')"
         style="max-width:200px;max-height:200px;border-radius:8px;cursor:pointer;box-shadow:0 2px 8px rgba(0,0,0,0.1);object-fit:cover;display:inline-block" title="点击放大查看">
  </div>`
}

function appendCreateToChat(prompt: string, htmlContent: string, downloadUrl: string, roleKey: string) {
  // ★ 创作模式下写到 create-chat-history，而不是 chat-messages
  const createPanel = document.getElementById('create-panel')
  const isCreateMode = createPanel && !createPanel.classList.contains('hidden')
  const container = isCreateMode
    ? document.getElementById('create-chat-history')
    : chatMessagesRef.value
  if (!container) return
  const emptyState = isCreateMode ? null : container.querySelector('.empty-state')
  if (emptyState) emptyState.remove()

  if (prompt) {
    const uw = document.createElement('div')
    uw.className = 'message-wrapper user'
    uw.appendChild(createRoleLabel('user', 'user'))
    const ub = document.createElement('div')
    ub.className = 'chat-bubble user'
    ub.textContent = prompt
    uw.appendChild(ub)
    container.appendChild(uw)
  }

  if (htmlContent) {
    const aw = document.createElement('div')
    aw.className = 'message-wrapper ai'
    aw.appendChild(createRoleLabel(CFG.ASSISTANT_NAME, 'ai'))
    const ab = document.createElement('div')
    ab.className = 'chat-bubble ai media-bubble'
    Object.assign(ab.style, { width: '100%', maxWidth: 'none', boxSizing: 'border-box', alignSelf: 'stretch', textAlign: 'center', whiteSpace: 'normal' })
    ab.innerHTML = htmlContent
    aw.appendChild(ab)
    if (downloadUrl) aw.dataset.downloadUrl = downloadUrl
    if (prompt) aw.dataset.userMessage = prompt
    container.appendChild(aw)
    addBubbleActions(ab, roleKey, false)
  }
  scrollToBottom()
}

function startVideoPoll(resultEl: HTMLElement | null, prompt: string) {
  if (!resultEl) return
  resultEl.innerHTML = '<div class="result-loading"><span class="spinner"></span>视频生成中，预计1-5分钟…<br><small>请勿关闭页面</small></div>'

  let pollCount = 0
  const maxPolls = 120
  let consecutive500 = 0

  function poll() {
    pollCount++
    const token = portal.getToken()
    const apiVersion = CFG.API_VERSION || '20260604'
    fetch('/api/media_proxy.php?v=' + apiVersion, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
      body: JSON.stringify({ action: 'video_query', task_id: videoTaskId }),
    }).then(async resp => {
      const rawText = await resp.text()
      let data: any
      try { data = JSON.parse(rawText) } catch (e) {
        resultEl!.innerHTML = '<div style="color:#ef4444;padding:10px;">'+sIcons.warn+' 服务器返回异常 (HTTP '+resp.status+')</div>'
        creating.value = false; videoTaskId = null; return
      }
      if (!data.success) {
        consecutive500++
        if (consecutive500 >= 5) {
          resultEl!.innerHTML = '<div style="color:#ef4444;padding:10px;">'+sIcons.warn+' 查询失败: '+escHtml(data.error || '')+'</div>'
          creating.value = false; videoTaskId = null; return
        }
        videoPollTimer = setTimeout(poll, 5000); return
      }
      consecutive500 = 0
      if (data.data?.status === 'SUCCEEDED') {
        const url = data.data.display_url || data.data.local_url || data.data.video_url || data.data.url || ''
        resultEl!.innerHTML = `<video controls src="${escHtml(url)}" style="max-width:100%;max-height:400px;border-radius:8px"></video>
          <div class="result-actions"><button class="result-btn" onclick="window.open('${escHtml(url)}','_blank')">${sIcons.dl} 下载视频</button></div>`
        creating.value = false; videoTaskId = null
        createPrompt.value = ''
        globalToast('视频生成完成！', 'success')
        appendCreateToChat(prompt, `<div><video controls src="${escHtml(url)}" style="max-width:100%;max-height:360px;border-radius:8px"></video></div>`, '', createTab.value === 'video' ? 'video' : 'video_i2v')
        sidebarRef.value?.loadHistory()
      } else if (data.data?.status === 'FAILED') {
        resultEl!.innerHTML = '<div style="color:#ef4444;padding:10px;">'+sIcons.warn+' 视频生成失败: '+escHtml(data.data.error || '')+'</div>'
        creating.value = false; videoTaskId = null
      } else if (pollCount >= maxPolls) {
        resultEl!.innerHTML = '<div style="color:#f59e0b;padding:10px;">'+sIcons.alarm+' 视频生成超时</div>'
        creating.value = false; videoTaskId = null
      } else {
        resultEl!.innerHTML = `<div class="result-loading"><span class="spinner"></span>视频生成中… (${pollCount}/${maxPolls})</div>`
        videoPollTimer = setTimeout(poll, 5000)
      }
    }).catch(() => {
      if (pollCount < maxPolls) videoPollTimer = setTimeout(poll, 5000)
      else { creating.value = false; videoTaskId = null }
    })
  }
  poll()
}

function resetUploadUI() {
  const preview = document.getElementById('i2i-preview') as HTMLImageElement
  const placeholder = document.getElementById('i2i-placeholder')
  const uploadArea = document.getElementById('upload-area')
  if (preview) { preview.classList.add('hidden'); preview.src = '' }
  if (placeholder) placeholder.style.display = ''
  if (uploadArea) uploadArea.classList.remove('has-image')
  if (i2iFileInput.value) i2iFileInput.value.value = ''
}

// ---- Global Mode ----
function setGlobalMode(mode: 'chat' | 'create' | 'debate' | 'zen') {
  // 禅模式走确认弹窗流程，不直接切换
  if (mode === 'zen') {
    requestZenMode()
    return
  }
  // 如果从禅模式切到其他模式，先退出禅模式
  if (isZenActive.value && mode !== 'zen') {
    exitZenMode()
  }

  portal.state.globalMode = mode
  const chatArea = document.getElementById('chat-area')
  const createPanel = document.getElementById('create-panel')
  const inputArea = document.getElementById('input-area')
  const createInputArea = document.getElementById('create-input-area')
  const navDots = document.getElementById('chat-nav-dots')
  const modeSelector = document.getElementById('mode-selector-area')

  if (mode === 'chat') {
    if (chatArea) chatArea.style.display = ''
    if (createPanel) createPanel.classList.add('hidden')
    if (inputArea) inputArea.style.display = ''
    if (createInputArea) createInputArea.classList.add('hidden')
    if (navDots) navDots.style.display = ''
    if (modeSelector) modeSelector.style.display = ''
  } else if (mode === 'debate') {
    if (chatArea) chatArea.style.display = ''
    if (createPanel) createPanel.classList.add('hidden')
    if (inputArea) inputArea.style.display = ''
    if (createInputArea) createInputArea.classList.add('hidden')
    if (navDots) navDots.style.display = ''
    if (modeSelector) modeSelector.style.display = 'none'
  } else if (mode === 'create') {
    if (chatArea) chatArea.style.display = 'none'
    if (createPanel) createPanel.classList.remove('hidden')
    if (inputArea) inputArea.style.display = 'none'
    if (createInputArea) createInputArea.classList.remove('hidden')
    if (navDots) navDots.style.display = 'none'
  }

  // 切换模式时关闭右侧面板（我的分享/创作成果/详情），回到正常对话
  rightPanel.value = 'chat'

  try { localStorage.setItem('portal_global_mode', mode) } catch (_) {}
}

// ---- Zen Mode ----
function requestZenMode() {
  showZenConfirm.value = true
}

function cancelZenEnter() {
  showZenConfirm.value = false
  // 恢复侧边栏的活动按钮为实际激活模式
}

async function confirmEnterZen() {
  showZenConfirm.value = false
  zenEntering.value = true

  // 播放过渡动画
  const transitionEl = zenTransitionRef.value
  if (transitionEl) {
    transitionEl.classList.add('active')
    await new Promise(r => setTimeout(r, 800))
    transitionEl.classList.remove('active')
  }

  // 设置为 zen 模式
  portal.state.globalMode = 'zen'
  isZenActive.value = true
  zenEntering.value = false

  // 设置 DOM
  const chatArea = document.getElementById('chat-area')
  const createPanel = document.getElementById('create-panel')
  const inputArea = document.getElementById('input-area')
  const createInputArea = document.getElementById('create-input-area')
  const navDots = document.getElementById('chat-nav-dots')
  const modeSelector = document.getElementById('mode-selector-area')

  if (chatArea) chatArea.style.display = ''
  if (createPanel) createPanel.classList.add('hidden')
  if (inputArea) inputArea.style.display = ''
  if (createInputArea) createInputArea.classList.add('hidden')
  if (navDots) navDots.style.display = ''
  if (modeSelector) modeSelector.style.display = ''

  // 关闭右侧面板
  rightPanel.value = 'chat'

  // 折叠侧边栏
  sidebarRef.value?.collapse?.()

  // 聚焦输入框
  await nextTick()
  chatInputRef.value?.focus()

  try { localStorage.setItem('portal_global_mode', 'zen') } catch (_) {}
}

// 直接进入禅模式（历史对话点击用，跳过确认弹窗和动画）
function enterZenDirect() {
  if (isZenActive.value) return  // 已经在禅模式
  // 如果之前在 create/debate 等其他模式，先切 UI
  isZenActive.value = true
  portal.state.globalMode = 'zen'
  const chatArea = document.getElementById('chat-area')
  const createPanel = document.getElementById('create-panel')
  const inputArea = document.getElementById('input-area')
  const createInputArea = document.getElementById('create-input-area')
  const navDots = document.getElementById('chat-nav-dots')
  const modeSelector = document.getElementById('mode-selector-area')
  if (chatArea) chatArea.style.display = ''
  if (createPanel) createPanel.classList.add('hidden')
  if (inputArea) inputArea.style.display = ''
  if (createInputArea) createInputArea.classList.add('hidden')
  if (navDots) navDots.style.display = ''
  if (modeSelector) modeSelector.style.display = ''
  sidebarRef.value?.collapse?.()
  rightPanel.value = 'chat'
  try { localStorage.setItem('portal_global_mode', 'zen') } catch (_) {}
}

function exitZenMode() {
  isZenActive.value = false
  // 恢复侧边栏
  sidebarRef.value?.expand?.()

  // 切回 chat 模式
  portal.state.globalMode = 'chat'
  const chatArea = document.getElementById('chat-area')
  const navDots = document.getElementById('chat-nav-dots')
  const modeSelector = document.getElementById('mode-selector-area')
  if (chatArea) chatArea.style.display = ''
  if (navDots) navDots.style.display = ''
  if (modeSelector) modeSelector.style.display = ''

  try { localStorage.setItem('portal_global_mode', 'chat') } catch (_) {}
}

// ---- Keyboard shortcuts ----
function onGlobalKeydown(e: KeyboardEvent) {
  if (e.ctrlKey && e.key === 'n') {
    e.preventDefault()
    newChat()
    return
  }
  if ((e.ctrlKey && e.key === '/') || (e.ctrlKey && e.key === 'k')) {
    e.preventDefault()
    chatInputRef.value?.focus()
  }
}

// ---- Visibility ----
function onVisibilityChange() {
  tabHidden = document.hidden
}

// ---- Scroll (userScrolledUp) ----
function onChatScroll() {
  const chatArea = document.getElementById('chat-area')
  if (!chatArea) return
  const atBottom = chatArea.scrollHeight - chatArea.scrollTop - chatArea.clientHeight
  userScrolledUp = atBottom > 60
}

// ---- Load Conversation History (Chat) ----
async function loadConversation(requestId: string) {
  // 设置当前 requestId 和 conversationId，供分享等功能使用
  portal.state.currentRequestId = requestId || null
  // 侧边栏的 groupId = conversation_id，同步为 currentConversationId
  if (requestId) portal.state.currentConversationId = requestId
  // Clear current chat
  const container = chatMessagesRef.value
  if (container) {
    container.querySelectorAll('.message-wrapper,.debate-phase-sep,.phase-separator').forEach((el: Element) => el.remove())
  }
  messages.length = 0
  inputCentered.value = false

  try {
    const data = await portal.apiGet('dashboard.php', { action: 'conversations', request_id: requestId })
    const msgs = data.data || data.rows || []
    if (msgs.length === 0) return

    const turns: { request_id: string; user_message: string | null; user_created_at: string | null; roles: any[]; summaries: any[]; files: any[] }[] = []
    let currentTurn: any = null
    msgs.forEach((m: any) => {
      const rid = m.request_id || ''
      if (!currentTurn || currentTurn.request_id !== rid) {
        currentTurn = { request_id: rid, user_message: null, user_created_at: null, roles: [], summaries: [], files: [] }
        turns.push(currentTurn)
      }
      if (m.user_message && !currentTurn.user_message) {
        currentTurn.user_message = m.user_message.replace(/\s*\[图\/文件：[^\]]*\]/g, '')
        currentTurn.user_created_at = m.created_at || null
      }
      if (m._files && Array.isArray(m._files) && m._files.length > 0 && currentTurn.files.length === 0) {
        currentTurn.files = m._files
      }
      if (m.is_summary == 1) {
        if (m.role_key === 'multimodal') currentTurn.roles.push(m)
        else currentTurn.summaries.push(m)
      } else if (m.role_response) {
        currentTurn.roles.push(m)
      }
    })

    // 合并没有 user_message 的幽灵 turn 到相邻 turn
    // 但含有多模态角色的幽灵 turn 保留独立（补一个占位 user_message），避免标签错乱
    const cleanTurns: typeof turns = []
    for (let ti = 0; ti < turns.length; ti++) {
      const t = turns[ti]
      const hasMultimodal = t.roles.some((r: any) => r.role_key === 'multimodal')
      // 多模态幽灵 turn：补占位 user_message 保持独立
      if (!t.user_message && hasMultimodal) {
        t.user_message = sIcons.img+' 图片分析'
        cleanTurns.push(t)
      } else if (!t.user_message && cleanTurns.length > 0) {
        const prev = cleanTurns[cleanTurns.length - 1]
        prev.roles = prev.roles.concat(t.roles)
        prev.summaries = prev.summaries.concat(t.summaries)
        if (t.files && t.files.length > 0 && prev.files.length === 0) prev.files = t.files
      } else if (!t.user_message && ti + 1 < turns.length) {
        const next = turns[ti + 1]
        next.roles = next.roles.concat(t.roles)
        next.summaries = next.summaries.concat(t.summaries)
        if (t.files && t.files.length > 0 && next.files.length === 0) next.files = t.files
      } else {
        cleanTurns.push(t)
      }
    }

    // 深度思辨 SVG 图标（与实时辩论一致）
    const debateSvgIcons: Record<string, string> = {
      judge: '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="3" x2="12" y2="8"/><circle cx="12" cy="13" r="4"/><line x1="8" y1="21" x2="16" y2="21"/></svg>',
      builder: '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="12 2 22 8.5 22 15.5 12 22 2 15.5 2 8.5 12 2"/><line x1="12" y1="22" x2="12" y2="14"/><polyline points="22 8.5 12 14 2 8.5"/></svg>',
      reviewer: '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>',
    }
    // 辩论阶段定义：按 role_key 顺序排列 { label, icon, cssClass }
    const debatePhases: { rk: string, label: string, icon: string, cssClass: string }[] = [
      { rk: 'debate_calibrator', label: '裁判官拆解', icon: debateSvgIcons.judge, cssClass: 'debate-calibrator' },
      { rk: 'debate_left',       label: '建构者方案', icon: debateSvgIcons.builder, cssClass: 'debate-left' },
      { rk: 'debate_right',      label: '审视者批判', icon: debateSvgIcons.reviewer, cssClass: 'debate-right' },
      { rk: 'debate_left',       label: '建构者修订', icon: debateSvgIcons.builder, cssClass: 'debate-left' },
      { rk: 'debate_calibrator', label: '裁判官审定', icon: debateSvgIcons.judge, cssClass: 'debate-calibrator' },
    ]

    function renderDebateTurn(turn: any, idx: number) {
      if (idx > 0) {
        const sep = document.createElement('div')
        sep.className = 'debate-phase-sep debate-calibrator'
        sep.innerHTML = '<span class="debate-phase-line"></span><span class="debate-phase-icon">'
          + debateSvgIcons.judge + '</span><span class="debate-phase-label">新一轮思辨</span><span class="debate-phase-line"></span>'
        chatMessagesRef.value?.appendChild(sep)
      }
      if (turn.user_message) {
        appendMessage('user', turn.user_message, 'user', false, '', turn.user_created_at || '')
      }

      // 过滤辩论角色（非 multimodal）
      const dbRoles = turn.roles.filter((r: any) => r.role_key !== 'multimodal')
      const dbSummaries = turn.summaries.filter((s: any) => s.role_key !== 'multimodal')

      // 按顺序渲染：先角色后总结
      let phaseIdx = 0
      dbRoles.forEach((role: any) => {
        const phase = debatePhases[phaseIdx]
        if (phase) {
          addDebatePhaseSeparator(phase.label, phase.cssClass, phase.icon)
        }
        let resp = role.role_response || ''
        // 拆解阶段：优先用 HTML；若无 HTML 但有 _sub_problems，重建列表
        let isDecomposeHTML = false
        if (phaseIdx === 0) {
          if (resp.includes('debate-sub-list') || resp.includes('<ol') || resp.includes('<li')) {
            isDecomposeHTML = true
          } else {
            // 尝试从 _sub_problems 重建拆解 HTML
            let subProblems: any[] = []
            try {
              if (role._sub_problems) {
                subProblems = typeof role._sub_problems === 'string' ? JSON.parse(role._sub_problems) : role._sub_problems
              }
            } catch (_) {}
            if (subProblems.length > 0) {
              let html = resp ? `<p>${escHtml(resp)}</p>` : ''
              html += '<ol class="debate-sub-list">'
              subProblems.forEach((sp: any) => {
                const confirmTag = sp.needs_confirm ? ' <span class="debate-confirm-tag">需确认</span>' : ''
                html += `<li>${escHtml(sp.question || sp)}${confirmTag}</li>`
              })
              html += '</ol>'
              resp = html
              isDecomposeHTML = true
            }
          }
        }
        const cssCls = phase ? ('ai ' + (isDecomposeHTML ? 'debate-html' : 'markdown') + ' ' + phase.cssClass) : 'ai markdown'
        const bubble = appendMessage('ai', isDecomposeHTML ? resp : renderMarkdown(resp), '', false, cssCls) as HTMLElement
        if (bubble) addBubbleActions(bubble, role.role_key, false, role.created_at)
        phaseIdx++
      })

      // 审定（总结）
      dbSummaries.forEach((s: any) => {
        const phase = debatePhases[phaseIdx]
        if (phase) {
          addDebatePhaseSeparator(phase.label, phase.cssClass, phase.icon)
        }
        const resp = s.role_response || ''
        const cssCls = phase ? ('ai markdown ' + phase.cssClass) : 'summary markdown'
        const bubble = appendMessage('ai', renderMarkdown(resp), '', false, cssCls) as HTMLElement
        if (bubble) addBubbleActions(bubble, 'debate_verdict', false, s.created_at)
        phaseIdx++
      })
    }

    function isDebateTurn(turn: any): boolean {
      const allDebateRoles = [...turn.roles, ...turn.summaries]
      if (allDebateRoles.length === 0) return false
      // 双重检测：role_key 以 debate_ 开头，或 role_name 是辩论角色名（兼容旧数据）
      const debateNames = ['校准管', '左脑', '右脑', '裁判官', '建构者', '审视者']
      const debateCount = allDebateRoles.filter((r: any) => {
        const rk = (r.role_key || '').toLowerCase()
        const rn = (r.role_name || '')
        return rk.startsWith('debate_') || debateNames.includes(rn)
      }).length
      const result = debateCount >= allDebateRoles.length * 0.5 && debateCount >= 1
      return result
    }

    cleanTurns.forEach((turn: any, idx: number) => {
      // 检测是否为辩论轮次
      if (isDebateTurn(turn)) {
        renderDebateTurn(turn, idx)
        return
      }
      if (idx > 0) {
        addPhaseSeparator('追问')
      }
      if (turn.user_message) {
        appendMessage('user', turn.user_message, 'user', false, '', turn.user_created_at || '')
      }

      // Split multimodal from regular roles
      const multimodalRoles = turn.roles.filter((r: any) => r.role_key === 'multimodal')
      const regularRoles = turn.roles.filter((r: any) => r.role_key !== 'multimodal')
      const hasMultimodal = multimodalRoles.length > 0

      if (hasMultimodal) {
        addPhaseSeparator('多模态感知')
        multimodalRoles.forEach((r: any) => {
          const bubble = appendMessage('ai', renderMarkdown(r.role_response || ''), '多模态感知', false, 'ai markdown') as HTMLElement
          if (bubble) addBubbleActions(bubble, r.role_key, false, r.created_at)
        })
      }

      // ---- Summaries (联动总结) 提前到角色之前 ----
      // 收集非创作类的 summary（联动总结）
      const normalSummaries = turn.summaries.filter((s: any) => {
        const isCreationMedia = ['text2img', 'img2img', 'video_gen', 'video', 'video_create'].includes(s.role_key)
        return !isCreationMedia && s.role_key !== 'dispatcher'
      })
      const hasNormalSummary = normalSummaries.length > 0

      // 联动总结放在角色之前
      if (!hasMultimodal && hasNormalSummary && regularRoles.length > 1) {
        addPhaseSeparator('联动总结')
        normalSummaries.forEach((s: any) => {
          const bubble = appendMessage('ai', renderMarkdown(s.role_response || ''), '联动总结', false, 'summary markdown') as HTMLElement
          if (bubble) {
            renderSummaryRefLinks(bubble)
            addBubbleActions(bubble, 'summary', false, s.created_at)
          }
        })
      }

      // 角色回答（默认折叠）
      if (regularRoles.length > 1) {
        addPhaseSeparator('多角色讨论')
      }
      regularRoles.forEach((role: any) => {
        const label = (role.role_key || '').toLowerCase().includes('dispatcher') ? '调度中心' : (role.role_name || role.role_key || 'AI')
        const resp = role.role_response || ''
        // Media render
        if (resp.startsWith('[video_task:') || resp.startsWith('[bailian_video:')) {
          appendMessage('ai', '<div style="padding:16px;text-align:center;color:var(--text-secondary)">'+sIcons.play+' 视频生成中…</div>', label, false, 'ai')
        } else if (resp.startsWith('{') && (resp.includes('"d"') || resp.includes('"url"'))) {
          try {
            const p = JSON.parse(resp)
            const url = p.d || p.url || p.display_url || ''
            if (url) {
              const isV = url.includes('.mp4') || role.role_key === 'video' || role.role_key === 'video_gen'
              if (isV) {
                appendMessage('ai', `<div style="text-align:center"><video controls src="${escHtml(url)}" style="max-width:100%;max-height:300px;border-radius:8px"></video></div>`, label, false, 'ai media')
              } else {
                appendMessage('ai', `<div class="media-result-thumb" style="text-align:center"><img src="${escHtml(url)}" alt="AI生成" onclick="__openLightbox('${escHtml(url)}')" style="max-width:200px;max-height:200px;border-radius:8px;cursor:pointer;box-shadow:0 2px 8px rgba(0,0,0,0.1);object-fit:cover"></div>`, label, false, 'ai media')
              }
            }
          } catch (_) {
            const bubble = appendMessage('ai', renderMarkdown(resp), label, false, 'ai markdown') as HTMLElement
            if (bubble) {
              addBubbleActions(bubble, role.role_key, false, role.created_at)
              collapseBubble(bubble)
            }
          }
        } else {
          const bubble = appendMessage('ai', renderMarkdown(resp), label, false, 'ai markdown') as HTMLElement
          if (bubble) {
            addBubbleActions(bubble, role.role_key, false, role.created_at)
            collapseBubble(bubble)
          }
        }
      })

      // ---- AI创作媒体 Summaries ----
      if (!hasMultimodal && turn.summaries.length > 0) {
        turn.summaries.forEach((s: any) => {
          const isCreationMedia = ['text2img', 'img2img', 'video_gen', 'video', 'video_create'].includes(s.role_key)
          if (isCreationMedia && s.role_response) {
            const raw = s.role_response.trim()
            if (/^\[video_task:/.test(raw) || /^\[bailian_video:/.test(raw)) {
              addPhaseSeparator(CFG.ASSISTANT_NAME)
              appendMessage('ai', '<div style="padding:16px;text-align:center">'+sIcons.play+' 视频生成中，请稍候…</div>', CFG.ASSISTANT_NAME, false, 'ai')
              return
            }
            let displayUrl = raw, downloadUrl = raw
            try {
              const p = JSON.parse(raw)
              if (p.d) { displayUrl = p.d; downloadUrl = p.o || p.d }
            } catch (_) {}
            if (displayUrl && /^(https?:\/\/|\/uploads\/|\/)/.test(displayUrl)) {
              addPhaseSeparator(CFG.ASSISTANT_NAME)
              const isV = s.role_key === 'video' || s.role_key === 'video_create' || s.role_key === 'video_gen'
              if (isV) {
                appendMessage('ai', `<div style="text-align:center"><video controls src="${escHtml(displayUrl)}" style="max-width:100%;max-height:300px;border-radius:8px"></video></div>`, CFG.ASSISTANT_NAME, false, 'ai media')
              } else {
                const wrapper = appendMessage('ai', `<div class="media-result-thumb" style="text-align:center"><img src="${escHtml(displayUrl)}" alt="AI创作" onclick="__openLightbox('${escHtml(displayUrl)}')" style="max-width:200px;max-height:200px;border-radius:8px;cursor:pointer;box-shadow:0 2px 8px rgba(0,0,0,0.1);object-fit:cover;display:inline-block"></div>`, CFG.ASSISTANT_NAME, false, 'ai media')
                if (wrapper && wrapper.parentElement && downloadUrl) {
                  ;(wrapper.parentElement as HTMLElement).dataset.downloadUrl = downloadUrl
                }
              }
              return
            }
          }
          // Dispatcher self-reply (only when no other regular roles)
          if (s.role_key === 'dispatcher' && regularRoles.length === 0) {
            const bubble = appendMessage('ai', renderMarkdown(s.role_response || ''), CFG.ASSISTANT_NAME, false, 'ai markdown') as HTMLElement
            if (bubble) addBubbleActions(bubble, 'self_reply', false, s.created_at)
          }
        })
      }
    })

    // Scroll to bottom
    nextTick(() => {
      const chatArea = document.getElementById('chat-area')
      if (chatArea) chatArea.scrollTop = chatArea.scrollHeight
      setTimeout(updateNavDots, 300)
    })

    // Update sidebar current conv
    if (sidebarRef.value) {
      sidebarRef.value.currentConvId = requestId
    }
  } catch (e: any) {
    globalToast('加载对话失败: ' + (e.message || '网络错误'), 'error')
  }
}

// ---- Load Creation History ----
async function loadCreationHistory(requestId: string) {
  // 设置当前 requestId，供分享等功能使用
  portal.state.currentRequestId = requestId || null
  // Switch to create mode
  setGlobalMode('create')
  if (sidebarRef.value) sidebarRef.value.currentConvId = requestId

  const resultEl = document.getElementById('create-result')
  const chatHistory = document.getElementById('create-chat-history')
  if (resultEl) { resultEl.innerHTML = ''; resultEl.classList.remove('active') }
  if (chatHistory) chatHistory.innerHTML = ''
  currentGroupId = requestId

  try {
    const token = portal.getToken()
    const baseUrl = (CFG.API_BASE || '') + '/api/dashboard.php'
    const resp = await fetch(baseUrl + '?action=conversations&request_id=' + encodeURIComponent(requestId) + '&_t=' + Date.now(), {
      headers: { 'Authorization': 'Bearer ' + token },
      cache: 'no-store',
    })
    const data = await resp.json()
    const messages = data.data || data.rows || []
    if (messages.length === 0) return

    const firstMsg = messages[0] || {}
    const prompt = firstMsg.user_message || ''
    const roleKey = firstMsg.role_key || ''
    const raw = firstMsg.role_response || ''
    const isVideo = roleKey === 'video' || roleKey === 'video_create'

    // Parse media URLs
    let display = raw, download = raw
    try {
      const p = JSON.parse(raw)
      if (p.d) { display = p.d; download = p.o || p.d }
    } catch (_) {}

    // Switch to matching tab
    if (roleKey === 'text2img') switchCreateTab('text2img')
    else if (roleKey === 'img2img') switchCreateTab('img2img')
    else if (roleKey === 'video' || roleKey === 'video_create') {
      switchCreateTab('video')
      if (raw.match(/^\[video_task:/) || raw.match(/^\[bailian_video:/)) {
        // Video task still running
        if (chatHistory) chatHistory.innerHTML = '<div style="padding:16px;text-align:center;color:var(--text-secondary)">'+sIcons.play+' 视频生成中，请稍候…</div>'
        return
      }
    }

    // Render in create-chat-history
    const createPanel = document.getElementById('create-panel-inner')
    let histEl = chatHistory
    if (!histEl && createPanel) {
      histEl = document.createElement('div')
      histEl.id = 'create-chat-history'
      histEl.className = 'create-chat-history'
      createPanel.insertBefore(histEl, createPanel.firstChild)
    }
    if (!histEl || !display) return

    // User message
    if (prompt) {
      const uw = document.createElement('div')
      uw.className = 'message-wrapper user'
      uw.appendChild(createRoleLabel('user', 'user'))
      const ub = document.createElement('div')
      ub.className = 'chat-bubble user'
      ub.textContent = prompt
      uw.appendChild(ub)
      histEl.appendChild(uw)
    }

    // AI message
    const aw = document.createElement('div')
    aw.className = 'message-wrapper ai'
    if (prompt) aw.dataset.userMessage = prompt
    aw.appendChild(createRoleLabel(CFG.ASSISTANT_NAME, 'ai'))
    const ab = document.createElement('div')
    ab.className = 'chat-bubble ai media-bubble'
    Object.assign(ab.style, { width: '100%', maxWidth: 'none', boxSizing: 'border-box', alignSelf: 'stretch', textAlign: 'center', whiteSpace: 'normal' })
    if (isVideo) {
      ab.innerHTML = `<video controls src="${escHtml(display)}" style="max-width:100%;max-height:300px;border-radius:8px"></video>`
    } else {
      ab.innerHTML = `<div class="media-result-thumb" style="text-align:center"><img src="${escHtml(display)}" alt="AI生成" onclick="__openLightbox('${escHtml(display)}')" style="max-width:200px;max-height:200px;border-radius:8px;cursor:pointer;box-shadow:0 2px 8px rgba(0,0,0,0.1);object-fit:cover"></div>`
      if (download) aw.dataset.downloadUrl = download
    }
    aw.appendChild(ab)
    addBubbleActions(ab, roleKey, false, firstMsg.created_at || '')
    histEl.appendChild(aw)
  } catch (e: any) {
    console.error('loadCreationHistory error:', e)
    globalToast('加载创作记录失败: ' + (e.message || '网络错误'), 'error')
  }
}

// ---- Expose to window for onclick handlers ----
;(window as any).showLoginModal = showLoginModalFn
;(window as any).__openLightbox = openLightbox
;(window as any).__selectMention = selectMentionRole
;(window as any).__removeMention = removeMentionRole
;(window as any).__removeFile = removeFile
;(window as any).setGlobalMode = setGlobalMode
;(window as any).requestZenMode = requestZenMode
;(window as any).confirmEnterZen = confirmEnterZen
;(window as any).exitZenMode = exitZenMode
;(window as any).enterZenDirect = enterZenDirect
;(window as any).switchCreateTab = switchCreateTab
;(window as any).__confirmMultimodal = confirmMultimodal
;(window as any).__loadConversation = loadConversation
;(window as any).__loadCreationHistory = loadCreationHistory
;(window as any).__clearChat = () => {
  messages.length = 0
  const container = chatMessagesRef.value
  if (container) container.querySelectorAll('.message-wrapper,.debate-phase-sep,.phase-separator').forEach((el: Element) => el.remove())
  hideNavDots()
}
;(window as any).__newChat = newChat
;(window as any).closeLightbox = () => { showLightbox.value = false }
;(window as any).toggleFold = (id: string) => {
  document.getElementById(id)?.classList.toggle('open')
}

// ============ Init ============
onMounted(async () => {
  initTheme()
  await portal.loadSiteConfig()
  applySiteConfigToUI()

  const loggedIn = await portal.checkAuth()
  if (loggedIn) {
    updateLoginUI()
    sidebarRef.value?.loadHistory()
    sidebarRef.value?.loadRoles()
    sidebarRef.value?.loadUserProfile()
  } else {
    // 未登录：静默换取只读的访客演示身份，让访客直接看到精选演示数据
    const demoOk = await portal.demoLogin()
    if (demoOk) {
      updateLoginUI()
      sidebarRef.value?.loadHistory()
      sidebarRef.value?.loadRoles()
      sidebarRef.value?.loadUserProfile()
      globalToast('当前为演示模式：数据只读，仅可浏览', 'info')
    }
  }

  // Init global mode — 默认始终为正常对话模式
  portal.state.globalMode = 'chat'
  try { localStorage.setItem('portal_global_mode', 'chat') } catch (_) {}

  ready.value = true
  requestAnimationFrame(() => {
    document.getElementById('app-layout')?.classList.add('ready')
  })

  if (window.innerWidth <= 768) {
    mobileSidebarOpen.value = false
  }

  // Event listeners
  document.addEventListener('keydown', onGlobalKeydown)
  document.addEventListener('visibilitychange', onVisibilityChange)
  document.getElementById('chat-area')?.addEventListener('scroll', onChatScroll)
  document.addEventListener('click', closePopups)
  document.getElementById('settings-overlay')?.addEventListener('click', (e) => {
    if (e.target === document.getElementById('settings-overlay') && showSettings.value) {
      showSettings.value = false
    }
  })
  document.getElementById('login-modal-overlay')?.addEventListener('click', (e: any) => {
    if (e.target === e.currentTarget) showLogin.value = false
  })

  chatInputRef.value?.focus()
  sidebarRef.value?.initCollapse()

  // Image click → lightbox in chat messages
  chatMessagesRef.value?.addEventListener('click', (e) => {
    if ((e.target as HTMLElement)?.tagName === 'IMG' && (e.target as HTMLElement).closest('.chat-bubble')) {
      e.preventDefault()
      openLightbox((e.target as HTMLImageElement).src)
    }
  })
})

onUnmounted(() => {
  document.removeEventListener('keydown', onGlobalKeydown)
  document.removeEventListener('visibilitychange', onVisibilityChange)
  document.removeEventListener('click', closePopups)
})

// ---- SVG 图标集（替换 emoji）----
const S = {
  spin: '<svg class="s-icon s-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>',
  check: '<svg class="s-icon s-check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>',
  warn: '<svg class="s-icon s-warn" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>',
  edit: '<svg class="s-icon s-edit" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>',
  clip: '<svg class="s-icon s-clip" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>',
  img: '<svg class="s-icon s-img" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>',
  doc: '<svg class="s-icon s-doc" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>',
  dl: '<svg class="s-icon s-dl" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>',
  play: '<svg class="s-icon s-play" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="5 3 19 12 5 21 5 3"/></svg>',
  export_: '<svg class="s-icon s-export" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>',
  trash: '<svg class="s-icon s-trash" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>',
  pin: '<svg class="s-icon s-pin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="17" x2="12" y2="22"/><path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z"/></svg>',
  star: '<svg class="s-icon s-star" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>',
  starO: '<svg class="s-icon s-star-o" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>',
  mapPin: '<svg class="s-icon s-mappin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>',
  clock: '<svg class="s-icon s-clock" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>',
  alarm: '<svg class="s-icon s-alarm" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="13" r="8"/><path d="M12 9v4l2 2"/><path d="M4.5 5.5l-3 3"/><path d="M19.5 5.5l3 3"/></svg>',
  close: '<svg class="s-icon s-close" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>',
}
const sIcons = S

// ---- 联动总结占位 + 角色折叠 + 锚点跳转 ----

// 更新总结占位气泡的进度文本
function updateSummaryPlaceholder(collectedCount: number) {
  let total = (window as any).__totalDispatchedRoles || 0
  // 兜底：如果 total 还是 0（onDispatcherResult 没触发或没赋值），用 collectedCount 凑
  if (total <= 0) total = Math.max(collectedCount, 3)
  const container = chatMessagesRef.value
  if (!container) return
  const placeholderDiv = container.querySelector('.summary-placeholder') as HTMLElement
  if (placeholderDiv) {
    placeholderDiv.innerHTML = sIcons.spin+' 正在收集专家意见...<span class="summary-progress">'+collectedCount+'/'+total+'</span>'
  }
}

// 全部角色收集完毕，更新为"正在进行总结"
function updateSummaryPlaceholderToGenerating() {
  const container = chatMessagesRef.value
  if (!container) return
  const placeholderDiv = container.querySelector('.summary-placeholder') as HTMLElement
  if (placeholderDiv) {
    placeholderDiv.innerHTML = sIcons.edit+' 正在进行总结，请稍后...'
  }
}

// 查找总结占位气泡
function findSummaryPlaceholderBubble(): HTMLElement | null {
  const container = chatMessagesRef.value
  if (!container) return null
  // 必须找带 data-summary-placeholder 属性的气泡（避免拿到上一轮的已完成总结）
  const bubble = container.querySelector('.chat-bubble.summary[data-summary-placeholder]') as HTMLElement
  return bubble || null
}

// 将角色气泡的 markdown 渲染结果加上锚点 ID
function renderAnnotatedBubble(bubbleEl: HTMLElement, annotatedContent: string, roleKey: string) {
  if (!annotatedContent || !bubbleEl) return
  // 去掉后端添加的 [#N] 段落编号标记，避免渲染到页面上
  const cleanContent = annotatedContent.replace(/\[#\d+\]\s*/g, '')
  // 重新用 renderMarkdown 渲染
  bubbleEl.innerHTML = renderMarkdown(cleanContent)
  // 用 DOM 在段落/列表项/标题开头安全插入锚点，不破坏标签结构
  const blocks = bubbleEl.querySelectorAll('p, li, h1, h2, h3, h4, h5, h6, blockquote, pre')
  blocks.forEach((el, idx) => {
    const anchor = document.createElement('span')
    anchor.id = `role-line-${roleKey}-${idx + 1}`
    anchor.className = 'role-line-anchor'
    el.insertBefore(anchor, el.firstChild)
  })
}

// 在 summary 气泡中解析角色引用链接
// 匹配三种格式：详见角色名#N / 角色名#N / 裸 #N，统一替换为 ①②③
// 底部生成紧凑跳转列表
function renderSummaryRefLinks(bubbleEl: HTMLElement) {
  if (!bubbleEl) return

  // 模式1: 详见/参见/见 + 角色名#N  或  角色名#N（无前缀）
  // 角色名 = 2-12 个汉字，最后一字紧接 #N（允许中间有空格）
  const refA = /(?:详见|参见|见)?\s*([一-龥]{2,12})\s*#(\d+)/g
  // 模式2: 裸 #N（兜底，跳过 — 不替换为圆圈数字，避免出现重复编号）
  const refB = /#(\d+)/g

  const refList: { roleKey: string; roleName: string; lineNum: string }[] = []
  const circledNums = ['①','②','③','④','⑤','⑥','⑦','⑧','⑨','⑩','⑪','⑫','⑬','⑭','⑮','⑯','⑰','⑱','⑲','⑳']

  const walker = document.createTreeWalker(bubbleEl, NodeFilter.SHOW_TEXT)
  const textNodes: Text[] = []
  let n: Node | null
  while ((n = walker.nextNode()) !== null) textNodes.push(n as Text)

  // 先把所有文本节点的内容合并计算 slot，再回写到对应文本节点
  // 同一段落里可能跨越多个文本节点，因此分段（以 <p>/<li>/<h*> 边界为段）
  // 简化：逐节点处理
  textNodes.forEach(textNode => {
    const raw = textNode.textContent || ''

    interface Slot { start: number; end: number; roleName: string; lineNum: string; type: 'named' | 'bare' }
    const slots: Slot[] = []
    const consumed = new Set<string>()

    // 模式1: 含角色名的引用
    refA.lastIndex = 0
    let m: RegExpExecArray | null
    while ((m = refA.exec(raw)) !== null) {
      const key = `${m.index}-${m.index + m[0].length}`
      if (consumed.has(key)) continue
      consumed.add(key)
      const roleName = m[1].trim()
      // 跳过纯圆圈数字当角色名的情况（之前轮次替换的残留）
      if (!roleName || /^[①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳]+$/.test(roleName)) continue
      // 跳过纯标点当角色名的情况
      if (/^[（(【\[《〈「『]+$/.test(roleName)) continue
      slots.push({ start: m.index, end: m.index + m[0].length, roleName, lineNum: m[2], type: 'named' })
    }

    // 模式2: 裸 #N — 兜底，继承前一个 named slot 的角色名（必须在同一句内）
    // 不再走模式2时新建序号，避免重复编号
    refB.lastIndex = 0
    while ((m = refB.exec(raw)) !== null) {
      const key = `${m.index}-${m.index + m[0].length}`
      if (consumed.has(key)) continue
      const overlaps = slots.some(s => m.index >= s.start && m.index < s.end)
      if (overlaps) continue
      consumed.add(key)
      // 查找同段/同句中最近的 named slot 作为角色名来源
      // 用节点父元素（同段 = 同 p/li）找最近的 named slot
      const prevNamed = [...slots].reverse().find(s => s.type === 'named' && s.end < m.index)
      if (prevNamed) {
        // 复用 prevNamed 的角色名，作为同一段的延续引用
        // 但不入 refList，refList 里的索引是 prevNamed 自己的
        slots.push({
          start: m.index,
          end: m.index + m[0].length,
          roleName: prevNamed.roleName,
          lineNum: m[1],
          type: 'named',
        })
      }
      // 没有前序 named slot 时，跳过
    }

    if (slots.length === 0) return

    // 按位置排序并切分文本
    slots.sort((a, b) => a.start - b.start)
    const frag = document.createDocumentFragment()
    let cursor = 0
    slots.forEach(s => {
      if (s.start > cursor) {
        frag.appendChild(document.createTextNode(raw.slice(cursor, s.start)))
      }
      const roleKey = roleNameKeyMap.get(s.roleName) || s.roleName
      const existing = refList.findIndex(r => r.roleKey === roleKey && r.lineNum === s.lineNum)
      const idx = existing >= 0 ? existing : refList.length
      if (existing < 0) refList.push({ roleKey, roleName: s.roleName, lineNum: s.lineNum })
      const num = circledNums[idx] || `(${idx + 1})`
      const a = document.createElement('a')
      a.className = 'role-ref-link'
      a.href = '#'
      a.dataset.role = roleKey
      a.dataset.line = s.lineNum
      a.title = `点击跳转到 ${s.roleName} 的回答第${s.lineNum}段`
      a.textContent = num
      frag.appendChild(a)
      cursor = s.end
    })
    if (cursor < raw.length) {
      frag.appendChild(document.createTextNode(raw.slice(cursor)))
    }
    textNode.parentNode!.replaceChild(frag, textNode)
  })

  // 底部紧凑引用列表
  if (refList.length > 0) {
    const refBox = document.createElement('div')
    refBox.className = 'ref-list-box'
    const label = document.createElement('span')
    label.className = 'ref-list-label'
    label.textContent = '引用：'
    refBox.appendChild(label)
    refList.forEach((r, i) => {
      if (i > 0) refBox.appendChild(document.createTextNode(' '))
      const a = document.createElement('a')
      a.className = 'role-ref-link ref-list-item'
      a.href = '#'
      a.dataset.role = r.roleKey
      a.dataset.line = r.lineNum
      a.title = `点击跳转到 ${r.roleName} 的回答第${r.lineNum}段`
      a.textContent = circledNums[i] || `(${i + 1})`
      refBox.appendChild(a)
    })
    bubbleEl.appendChild(refBox)
  }
}

// 事件委托：summary 气泡内点击引用链接时跳转
document.addEventListener('click', (e: MouseEvent) => {
  // e.target 可能是文本节点，需取父元素
  let target = e.target as Node | null
  if (target && target.nodeType === Node.TEXT_NODE) {
    target = target.parentElement
  }
  const a = (target as HTMLElement)?.closest('a.role-ref-link')
  if (!a) return
  e.preventDefault()
  const role = a.dataset.role || ''
  const line = parseInt(a.dataset.line || '1')
  const fn = (window as any).__jumpToRoleLine
  if (typeof fn === 'function') {
    fn(role, line)
  } else {
    const el = document.getElementById(`role-line-${role}-${line}`)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      const bubble = el.closest('.chat-bubble')
      if (bubble && bubble.classList.contains('collapsed')) {
        bubble.classList.remove('collapsed')
      }
    }
  }
})

// 跳转到指定角色的指定行
function jumpToRoleLine(roleKey: string, lineNum: number) {
  const targetId = `role-line-${roleKey}-${lineNum}`
  const target = document.getElementById(targetId)
  if (!target) {
    // 找不到精确行号，尝试展开该角色的第一个气泡
    globalToast(`未找到 ${roleKey} 的第${lineNum}段，已展开该角色`, 'warn')
    expandRoleBubble(roleKey)
    return
  }
  // 先展开该角色气泡
  expandRoleBubble(roleKey)
  // 滚动到目标行
  setTimeout(() => {
    target.scrollIntoView({ behavior: 'smooth', block: 'center' })
    // 高亮动画
    target.classList.add('role-line-highlight')
    setTimeout(() => target.classList.remove('role-line-highlight'), 2000)
  }, 150)
}

// 展开指定角色的气泡
function expandRoleBubble(roleKey: string) {
  const container = chatMessagesRef.value
  if (!container) return
  // 遍历所有 ai message-wrapper，找角色名匹配的
  const wrappers = container.querySelectorAll('.message-wrapper.ai')
  wrappers.forEach((wrapper) => {
    const label = wrapper.querySelector('.role-label .role-name') as HTMLElement
    const labelText = label?.textContent?.trim() || ''
    const bubble = wrapper.querySelector('.chat-bubble') as HTMLElement
    if (bubble && bubble.classList.contains('collapsed')) {
      // 检查角色名是否匹配（模糊匹配）
      if (labelText.includes(roleKey) || roleKey.includes(labelText) ||
          labelText === roleKey) {
        bubble.classList.remove('collapsed')
        // 更新折叠按钮图标
        const collapseBtn = wrapper.querySelector('.bubble-btn-collapse') as HTMLElement
        if (collapseBtn) {
          collapseBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m18 15-6-6-6 6"/></svg>'
          collapseBtn.title = '折叠'
        }
        const roleLabel = wrapper.querySelector('.role-label') as HTMLElement
        if (roleLabel) roleLabel.style.display = 'flex'
      }
    }
  })
}

// 默认折叠气泡（不隐藏角色名）
function collapseBubble(bubbleEl: HTMLElement) {
  if (!bubbleEl) return
  bubbleEl.classList.add('collapsed')
  const wrapper = bubbleEl.parentElement
  if (wrapper) {
    const collapseBtn = wrapper.querySelector('.bubble-btn-collapse') as HTMLElement
    if (collapseBtn) {
      collapseBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m6 9 6 6 6-6"/></svg>'
      collapseBtn.title = '展开'
    }
  }
}

// 更新折叠按钮状态
function updateCollapseBtn(bubbleEl: HTMLElement, isCollapsed: boolean) {
  const wrapper = bubbleEl.parentElement
  if (!wrapper) return
  const collapseBtn = wrapper.querySelector('.bubble-btn-collapse') as HTMLElement
  if (!collapseBtn) return
  if (isCollapsed) {
    collapseBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m6 9 6 6 6-6"/></svg>'
    collapseBtn.title = '展开'
  } else {
    collapseBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m18 15-6-6-6 6"/></svg>'
    collapseBtn.title = '折叠'
  }
}

// 暴露到 window 供 HTML onclick 使用
;(window as any).__jumpToRoleLine = jumpToRoleLine

function closePopups(e: Event) {
  if (!(e.target as HTMLElement)?.closest('#btn-upload-toggle') && !(e.target as HTMLElement)?.closest('#upload-menu')) {
    document.getElementById('upload-menu')?.classList.add('hidden')
  }
  if (!(e.target as HTMLElement)?.closest('#mention-dropdown') && !(e.target as HTMLElement)?.closest('#chat-input')) {
    hideMentionDropdown()
  }
}
</script>
